/**
 * Kayasprasha Clinic - React Components
 * All major components in one file for easy reference
 */

import React, { useState, useEffect, useRef } from 'react';

// API Helper — same-origin in production ("/api" served by Flask at
// kayasparshaclinic.com/api), dev uses CRA proxy (see package.json).
const API_URL = process.env.REACT_APP_API_URL || '/api';
const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '919606209376';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// ==================== PATIENT SELECT COMPONENT ====================
// Reusable searchable patient dropdown used by InvoiceGenerator and MedicalForm
function PatientSelect({ patients, value, onChange, disabled, required, label }) {
  const [search, setSearch] = useState('');
  const selectedPatient = patients.find(p => String(p.id) === String(value)) || null;

  const filtered = search
    ? patients.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.patient_id.toLowerCase().includes(search.toLowerCase()) ||
        (p.phone || '').includes(search)
      )
    : patients;

  return (
    <div>
      <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{label || 'Select Patient *'}</label>
      <input
        type="text"
        placeholder="Search by name, ID or phone…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        disabled={disabled}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 6, boxSizing: 'border-box', color: '#1a1a1a', background: '#fff' }}
      />
      <select
        value={value}
        onChange={e => {
          const dbId = e.target.value;
          const pat = patients.find(p => String(p.id) === dbId) || null;
          onChange(dbId, pat);
        }}
        required={required}
        disabled={disabled || patients.length === 0}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#1a1a1a', background: '#fff' }}
      >
        <option value="">— Choose a patient —</option>
        {filtered.map(p => (
          <option key={p.id} value={p.id}>
            {p.patient_id} — {p.name}{p.age ? ` (${p.age}y` : ''}{p.sex ? `, ${p.sex})` : p.age ? ')' : ''}
          </option>
        ))}
      </select>
      {selectedPatient && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 16px', marginTop: 8, fontSize: 13, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <span>📋 <strong>{selectedPatient.patient_id}</strong></span>
          <span>👤 {selectedPatient.name}</span>
          {selectedPatient.age && <span>🎂 {selectedPatient.age} yrs</span>}
          {selectedPatient.sex && <span>⚥ {selectedPatient.sex}</span>}
          {selectedPatient.phone && <span>📞 {selectedPatient.phone}</span>}
          {selectedPatient.blood_group && <span>🩸 {selectedPatient.blood_group}</span>}
        </div>
      )}
    </div>
  );
}

// ==================== PRINT HELPERS ====================

function printPrescription(rx, patient) {
  const meds = Array.isArray(rx.oral_medications)
    ? rx.oral_medications
    : (rx.oral_medications || '').split('\n').filter(Boolean);
  const therapies = Array.isArray(rx.panchakarma_therapies)
    ? rx.panchakarma_therapies
    : (rx.panchakarma_therapies || '').split('\n').filter(Boolean);

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Prescription — ${rx.prescription_id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Georgia', serif; color: #1a1a1a; background: #fff; padding: 0; }
    .page { max-width: 800px; margin: 0 auto; padding: 30px 40px 40px; border: 2px solid #1a3a2a; min-height: 1050px; position: relative; }
    .header { border-bottom: 3px double #1a3a2a; padding-bottom: 16px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: flex-start; }
    .clinic-name { font-size: 26px; font-weight: bold; color: #1a3a2a; letter-spacing: 1px; }
    .clinic-tagline { font-size: 12px; color: #4a6741; margin-top: 3px; font-style: italic; }
    .clinic-contact { font-size: 11px; color: #555; text-align: right; line-height: 1.6; }
    .rx-meta { display: flex; justify-content: space-between; margin-bottom: 14px; font-size: 12px; color: #555; }
    .patient-box { background: #f0f7f0; border: 1px solid #b7d8b3; border-radius: 6px; padding: 12px 16px; margin-bottom: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; font-size: 13px; }
    .patient-box .field { display: flex; gap: 6px; }
    .patient-box .label { color: #555; min-width: 80px; }
    .patient-box .val { font-weight: 600; color: #1a1a1a; }
    .rx-symbol { font-size: 52px; color: #1a3a2a; font-family: 'Times New Roman', serif; line-height: 1; margin-bottom: 6px; }
    .section { margin-bottom: 18px; }
    .section-title { font-size: 13px; font-weight: bold; color: #1a3a2a; border-bottom: 1px solid #c8ddc5; padding-bottom: 4px; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
    .diag-box { background: #fffbf0; border-left: 4px solid #c5a400; padding: 10px 14px; font-size: 14px; font-weight: 600; margin-bottom: 4px; }
    .med-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .med-table th { background: #1a3a2a; color: #fff; padding: 7px 10px; text-align: left; font-size: 12px; }
    .med-table td { padding: 7px 10px; border-bottom: 1px solid #e8f0e8; vertical-align: top; }
    .med-table tr:nth-child(even) td { background: #f5f9f5; }
    .therapy-list { list-style: none; }
    .therapy-list li { padding: 5px 0 5px 18px; position: relative; font-size: 13px; border-bottom: 1px dashed #e2e8f0; }
    .therapy-list li::before { content: '✦'; position: absolute; left: 0; color: #1a3a2a; font-size: 10px; top: 7px; }
    .advice-text { font-size: 13px; line-height: 1.7; color: #333; white-space: pre-wrap; }
    .followup { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 10px 14px; font-size: 13px; color: #1e40af; font-weight: 600; margin-top: 16px; }
    .signature-area { margin-top: 40px; display: flex; justify-content: flex-end; }
    .signature-box { text-align: center; width: 200px; }
    .signature-line { border-top: 1px solid #1a3a2a; padding-top: 6px; font-size: 12px; color: #1a3a2a; font-weight: 600; }
    .footer { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px; text-align: center; font-size: 11px; color: #888; font-style: italic; }
    @media print {
      body { background: white; }
      .page { border: 2px solid #1a3a2a; }
      @page { margin: 15mm; size: A4; }
    }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div>
      <div class="clinic-name">🌿 KayaSparsha Ayurvedic Clinic</div>
      <div class="clinic-tagline">Authentic Panchakarma & Ayurvedic Wellness Centre</div>
      <div style="font-size:11px;color:#555;margin-top:4px;">Kengeri, Bangalore — www.kayasparshaclinic.com</div>
    </div>
    <div class="clinic-contact">
      📞 +91 9606209376<br/>
      ✉ support@kayasparsha.com<br/>
      Reg. No: 33433
    </div>
  </div>

  <div class="rx-meta">
    <span><strong>Rx ID:</strong> ${rx.prescription_id}</span>
    <span><strong>Date:</strong> ${new Date(rx.prescription_date || rx.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
  </div>

  <div class="patient-box">
    <div class="field"><span class="label">Patient:</span><span class="val">${patient?.name || rx.patient_name || '—'}</span></div>
    <div class="field"><span class="label">Patient ID:</span><span class="val">${patient?.patient_id || '—'}</span></div>
    <div class="field"><span class="label">Age / Sex:</span><span class="val">${patient?.age || rx.patient_age ? `${patient?.age || rx.patient_age} yrs` : '—'} / ${patient?.sex || rx.patient_sex || '—'}</span></div>
    <div class="field"><span class="label">Phone:</span><span class="val">${patient?.phone || rx.patient_phone || '—'}</span></div>
    <div class="field"><span class="label">Doctor:</span><span class="val">Dr. ${rx.doctor_name || '—'}</span></div>
    <div class="field"><span class="label">Visit Date:</span><span class="val">${new Date(rx.prescription_date || rx.created_at).toLocaleDateString('en-IN')}</span></div>
  </div>

  <div class="rx-symbol">℞</div>

  ${rx.diagnosis ? `<div class="section">
    <div class="section-title">Diagnosis</div>
    <div class="diag-box">${rx.diagnosis}</div>
  </div>` : ''}

  ${rx.treatment_notes ? `<div class="section">
    <div class="section-title">Treatment Plan</div>
    <div class="advice-text">${rx.treatment_notes}</div>
  </div>` : ''}

  ${meds.length > 0 ? `<div class="section">
    <div class="section-title">Oral Medications</div>
    <table class="med-table">
      <thead><tr><th>#</th><th>Medication</th><th>Dosage / Frequency</th><th>Duration</th></tr></thead>
      <tbody>
        ${meds.map((m, i) => {
          const parts = m.split(/[–—-]/).map(p => p.trim());
          return `<tr>
            <td>${i + 1}</td>
            <td><strong>${parts[0] || m}</strong></td>
            <td>${parts[1] || '—'}</td>
            <td>${parts[2] || (rx.duration_days ? rx.duration_days + ' days' : '—')}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
  </div>` : ''}

  ${therapies.length > 0 ? `<div class="section">
    <div class="section-title">Panchakarma / Therapies</div>
    <ul class="therapy-list">
      ${therapies.map(t => `<li>${t}</li>`).join('')}
    </ul>
  </div>` : ''}

  <div class="signature-area">
    <div class="signature-box">
      <div style="height:50px;"></div>
      <div class="signature-line">Dr. Shruthi Shetty<br/><span style="font-weight:400;font-size:11px;">B.A.M.S, MD · Ayurvedic Physician</span><br/><span style="font-weight:400;font-size:10px;color:#555;">Reg. No: 33433</span></div>
    </div>
  </div>

  <div class="footer">
    CONFIDENTIAL — AYURVEDIC PRESCRIPTION &nbsp;|&nbsp; KayaSparsha Clinic &nbsp;|&nbsp; Valid for 30 days from date of issue
  </div>
</div>
<script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

function printInvoice(inv) {
  const items = inv.items || [];
  const statusColors = { paid: '#15803d', pending: '#b45309', partial: '#1d4ed8' };
  const statusBg = { paid: '#dcfce7', pending: '#fef9c3', partial: '#dbeafe' };

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Invoice — ${inv.invoice_id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; color: #1a1a1a; background: #fff; padding: 0; }
    .page { max-width: 800px; margin: 0 auto; padding: 30px 40px 40px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 3px solid #1a3a2a; }
    .clinic-name { font-size: 24px; font-weight: bold; color: #1a3a2a; }
    .clinic-sub { font-size: 12px; color: #555; margin-top: 4px; }
    .invoice-title { font-size: 32px; font-weight: bold; color: #1a3a2a; text-align: right; }
    .invoice-id { font-size: 13px; color: #555; text-align: right; margin-top: 4px; }
    .meta-row { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
    .meta-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 14px; }
    .meta-box .title { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
    .meta-box .val { font-size: 14px; font-weight: 600; }
    .meta-box .sub { font-size: 12px; color: #555; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    thead tr { background: #1a3a2a; color: #fff; }
    th { padding: 10px 12px; text-align: left; font-size: 13px; }
    td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
    tr:last-child td { border-bottom: none; }
    .totals { width: 280px; margin-left: auto; }
    .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
    .totals-row.total { font-weight: bold; font-size: 16px; border-top: 2px solid #1a3a2a; padding-top: 10px; margin-top: 4px; color: #1a3a2a; }
    .status-badge { display: inline-block; padding: 4px 14px; border-radius: 12px; font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; background: ${statusBg[inv.payment_status] || '#f1f5f9'}; color: ${statusColors[inv.payment_status] || '#475569'}; }
    .footer { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 16px; text-align: center; font-size: 12px; color: #64748b; }
    @media print { @page { margin: 15mm; size: A4; } }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div>
      <div class="clinic-name">🌿 KayaSparsha Ayurvedic Clinic</div>
      <div class="clinic-sub">Kengeri, Bangalore &nbsp;|&nbsp; +91 9606209376 &nbsp;|&nbsp; support@kayasparsha.com</div>
    </div>
    <div>
      <div class="invoice-title">INVOICE</div>
      <div class="invoice-id">${inv.invoice_id}</div>
      <div style="margin-top:8px;text-align:right;"><span class="status-badge">${(inv.payment_status || 'pending').toUpperCase()}</span></div>
    </div>
  </div>

  <div class="meta-row">
    <div class="meta-box">
      <div class="title">Bill To</div>
      <div class="val">${inv.patient_name || '—'}</div>
      <div class="sub">${inv.patient_phone || ''}</div>
      <div class="sub">${inv.patient_email || ''}</div>
      <div class="sub">${inv.patient_address || ''}</div>
    </div>
    <div class="meta-box">
      <div class="title">Invoice Details</div>
      <div class="sub">Date: <strong>${new Date(inv.issued_date || inv.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></div>
      <div class="sub" style="margin-top:4px;">Doctor: ${inv.doctor_name || '—'}</div>
      <div class="sub" style="margin-top:4px;">Payment: ${inv.payment_method || 'Not specified'}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Service / Description</th>
        <th style="text-align:center;">Qty</th>
        <th style="text-align:right;">Unit Price</th>
        <th style="text-align:right;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${items.map((item, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${item.service_name || item.description || '—'}</td>
          <td style="text-align:center;">${item.quantity}</td>
          <td style="text-align:right;">₹${Number(item.unit_price).toFixed(2)}</td>
          <td style="text-align:right;">₹${Number(item.total_price).toFixed(2)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row"><span>Subtotal:</span><span>₹${Number(inv.subtotal || 0).toFixed(2)}</span></div>
    ${(() => {
      const sub = Number(inv.subtotal || 0);
      const tot = Number(inv.total_amount || 0);
      const disc = sub - tot;
      if (disc > 0.01) {
        const pct = sub > 0 ? ((disc / sub) * 100).toFixed(1) : '0';
        return `<div class="totals-row" style="color:#b91c1c;"><span>Discount (${pct}%):</span><span>−₹${disc.toFixed(2)}</span></div>`;
      }
      return '';
    })()}
    <div class="totals-row total"><span>Total:</span><span>₹${Number(inv.total_amount || 0).toFixed(2)}</span></div>
  </div>

  <div class="footer">
    Thank you for choosing KayaSparsha Ayurvedic Clinic &nbsp;🌿<br/>
    This is a computer-generated invoice. For queries: support@kayasparsha.com
  </div>
</div>
<script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}


// ==================== LOGIN COMPONENT ====================
export function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.user);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>🌿 Kayasprasha Clinic</h1>
        <h2>Staff Login</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Username or Email</label>
            <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="username or email@clinic.com" autoComplete="username" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter password" autoComplete="current-password" />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="btn btn-primary btn-large" style={{ width: '100%', marginTop: 8 }}>
            {loading ? 'Logging in…' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}


// ==================== NAVBAR COMPONENT ====================
export function Navbar({ user, onLogout, setCurrentPage }) {
  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <h1>🌿 Kayasprasha Clinic</h1>
      </div>
      <div className="navbar-menu">
        <button onClick={() => setCurrentPage('dashboard')} className="nav-btn">Dashboard</button>
        <button onClick={() => setCurrentPage('appointments')} className="nav-btn">📅 Appointments</button>

        {(user.role === 'receptionist' || user.role === 'admin' || user.role === 'doctor') && (
          <button onClick={() => setCurrentPage('register-patient')} className="nav-btn">👤 Register Patient</button>
        )}

        {/* Case Sheet: doctor and admin only — receptionist cannot create case sheets */}
        {(user.role === 'doctor' || user.role === 'admin') && (
          <button onClick={() => setCurrentPage('medical-form')} className="nav-btn">📋 Case Sheet</button>
        )}

        {(user.role === 'doctor' || user.role === 'admin') && (
          <button onClick={() => setCurrentPage('patient-history')} className="nav-btn">📜 Patient History</button>
        )}

        {/* Prescriptions: doctor, admin, receptionist (read + print only for receptionist) */}
        {(user.role === 'doctor' || user.role === 'admin' || user.role === 'receptionist') && (
          <button onClick={() => setCurrentPage('prescriptions')} className="nav-btn">💊 Prescriptions</button>
        )}

        {/* Invoices: doctor, receptionist, admin */}
        {(user.role === 'doctor' || user.role === 'receptionist' || user.role === 'admin') && (
          <button onClick={() => setCurrentPage('invoices')} className="nav-btn">🧾 Invoices</button>
        )}

        {/* Slots visible to all roles */}
        <button onClick={() => setCurrentPage('slots')} className="nav-btn">🗓 Slots</button>

        {(user.role === 'admin' || user.role === 'doctor') && (
          <button onClick={() => setCurrentPage('services')} className="nav-btn">🏥 Services</button>
        )}

        {(user.role === 'admin' || user.role === 'doctor') && (
          <button onClick={() => setCurrentPage('marketing')} className="nav-btn">📣 Marketing</button>
        )}

        {user.role === 'admin' && (
          <>
            <button onClick={() => setCurrentPage('reports')} className="nav-btn">Reports</button>
            <button onClick={() => setCurrentPage('users')} className="nav-btn">👥 Users</button>
          </>
        )}
      </div>
      <div className="navbar-user">
        <span>{user.name} ({user.role})</span>
        <button onClick={onLogout} className="btn-logout">Logout</button>
      </div>
    </nav>
  );
}


// ==================== DASHBOARD COMPONENT ====================
const STATUS_COLORS = {
  new:       { bg: '#eff6ff', color: '#1d4ed8', label: '🆕 New' },
  contacted: { bg: '#fef9c3', color: '#92400e', label: '📞 Contacted' },
  booked:    { bg: '#f0fdf4', color: '#15803d', label: '✅ Booked' },
  cancelled: { bg: '#fef2f2', color: '#b91c1c', label: '❌ Cancelled' },
};

export function Dashboard({ user, setCurrentPage }) {
  const [stats, setStats]         = useState({ total_patients: 0, new_enquiries: 0, today_appointments: 0, pending_invoices: 0, recent_patients: [] });
  const [enquiries, setEnquiries] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [updatingId, setUpdatingId]     = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoadingStats(true);
    try {
      const [sRes, aRes] = await Promise.all([
        fetch(`${API_URL}/stats`,        { headers: getAuthHeader() }),
        fetch(`${API_URL}/appointments?status=new`, { headers: getAuthHeader() }),
      ]);
      // If token expired, clear session and reload — login screen will appear
      if (sRes.status === 401 || aRes.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin';
        return;
      }
      const sData = await sRes.json();
      const aData = await aRes.json();
      setStats({ ...sData, recent_patients: sData.recent_patients || [] });
      setEnquiries(aData.appointments || []);
    } catch (e) {
      console.error('Dashboard fetch error', e);
    } finally {
      setLoadingStats(false);
    }
  };

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await fetch(`${API_URL}/appointments/${id}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ status }),
      });
      fetchAll();
    } finally {
      setUpdatingId(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const TH = ({ children, w }) => <th style={{ padding: '9px 12px', fontWeight: 600, background: '#f1f5f9', textAlign: 'left', whiteSpace: 'nowrap', width: w }}>{children}</th>;
  const TD = ({ children, style }) => <td style={{ padding: '9px 12px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'middle', ...style }}>{children}</td>;

  return (
    <div className="dashboard" style={{ padding: '24px 28px' }}>
      <h2 style={{ marginBottom: 6 }}>Welcome, {user.name}!</h2>
      <p style={{ color: '#64748b', marginBottom: 28, fontSize: 14 }}>Here's what's happening at the clinic today.</p>

      <div className="dashboard-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 16, marginBottom: 36 }}>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setCurrentPage('register-patient')}>
          <h3>Total Patients</h3>
          <p className="stat-value">{loadingStats ? '…' : stats.total_patients}</p>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer', borderTop: '3px solid #1d4ed8' }} onClick={() => setCurrentPage('appointments')}>
          <h3>New Enquiries</h3>
          <p className="stat-value" style={{ color: '#1d4ed8' }}>{loadingStats ? '…' : stats.new_enquiries}</p>
          <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Via website / chatbot</p>
        </div>
        <div className="stat-card">
          <h3>Today's Bookings</h3>
          <p className="stat-value">{loadingStats ? '…' : stats.today_appointments}</p>
        </div>
        {(user.role === 'receptionist' || user.role === 'admin') && (
          <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setCurrentPage('invoices')}>
            <h3>Pending Invoices</h3>
            <p className="stat-value" style={{ color: '#dc2626' }}>{loadingStats ? '…' : stats.pending_invoices}</p>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>🆕 New Website / Chatbot Enquiries</h3>
          <button className="btn" style={{ fontSize: 13 }} onClick={() => setCurrentPage('appointments')}>View All Appointments →</button>
        </div>
        {enquiries.length === 0 && !loadingStats ? (
          <p style={{ color: '#64748b', padding: '16px 0' }}>No new enquiries — you're all caught up! 🎉</p>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><TH>Name</TH><TH>Phone</TH><TH>Service Requested</TH><TH>Preferred Date/Time</TH><TH>Received</TH><TH>Action</TH></tr></thead>
              <tbody>
                {enquiries.map(a => (
                  <tr key={a.id} style={{ background: '#fff' }}>
                    <TD style={{ fontWeight: 500 }}>{a.name}</TD>
                    <TD><a href={`tel:${a.phone}`} style={{ color: '#0891b2', textDecoration: 'none' }}>{a.phone}</a></TD>
                    <TD>{a.service || '—'}</TD>
                    <TD>{a.preferred_date || '—'}</TD>
                    <TD style={{ fontSize: 12, color: '#64748b' }}>{fmtDate(a.created_at)}</TD>
                    <TD>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => updateStatus(a.id, 'contacted')} disabled={updatingId === a.id}
                          className="btn" style={{ fontSize: 12, padding: '4px 10px', background: '#fef9c3', color: '#92400e', border: '1px solid #d97706' }}>
                          📞 Contacted
                        </button>
                        <button onClick={() => updateStatus(a.id, 'booked')} disabled={updatingId === a.id}
                          className="btn" style={{ fontSize: 12, padding: '4px 10px', background: '#f0fdf4', color: '#15803d', border: '1px solid #16a34a' }}>
                          ✅ Booked
                        </button>
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>👥 Recent Patients</h3>
          {(user.role === 'receptionist' || user.role === 'admin') && (
            <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => setCurrentPage('register-patient')}>➕ Register New Patient</button>
          )}
        </div>
        {!loadingStats && stats.recent_patients.length === 0 ? (
          <p style={{ color: '#64748b' }}>No patients registered yet.</p>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><TH>Patient ID</TH><TH>Name</TH><TH>Age / Sex</TH><TH>Phone</TH><TH>Registered On</TH><TH>Action</TH></tr></thead>
              <tbody>
                {(stats.recent_patients || []).map(p => (
                  <tr key={p.id} style={{ background: '#fff' }}>
                    <TD><code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>{p.patient_id}</code></TD>
                    <TD style={{ fontWeight: 500 }}>{p.name}</TD>
                    <TD style={{ color: '#475569' }}>{p.age ? `${p.age} yrs` : '—'}{p.sex ? ` / ${p.sex}` : ''}</TD>
                    <TD><a href={`tel:${p.phone}`} style={{ color: '#0891b2', textDecoration: 'none' }}>{p.phone}</a></TD>
                    <TD style={{ fontSize: 12, color: '#64748b' }}>{fmtDate(p.created_at)}</TD>
                    <TD>
                      {(user.role === 'doctor' || user.role === 'admin') && (
                        <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setCurrentPage('medical-form')}>📋 Case Sheet</button>
                      )}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== PATIENT REGISTRATION COMPONENT ====================
export function PatientRegistration({ user }) {
  const emptyForm = { name: '', sex: '', age: '', phone: '', email: '', address: '', city: '', state: '', pincode: '', blood_group: '', allergies: '' };
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading]   = useState(false);
  const [message, setMessage]   = useState('');
  const [lastPatientId, setLastPatientId] = useState('');
  const [patients, setPatients] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Admin edit / delete state
  const [editPatient, setEditPatient]   = useState(null);   // patient object being edited
  const [editForm, setEditForm]         = useState({});
  const [editLoading, setEditLoading]   = useState(false);
  const [editMsg, setEditMsg]           = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);   // patient_id to confirm delete
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { fetchPatients(); }, []);

  const fetchPatients = async () => {
    setListLoading(true);
    try {
      const res = await fetch(`${API_URL}/patients`, { headers: getAuthHeader() });
      const data = await res.json();
      setPatients(data.patients || []);
    } catch (e) { console.error('Failed to load patients', e); }
    finally { setListLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const response = await fetch(`${API_URL}/patients`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setLastPatientId(data.patient.patient_id);
        setMessage('success');
        setFormData(emptyForm);
        fetchPatients();
      } else {
        setMessage(data.message || 'Error registering patient');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditPatient(p);
    setEditForm({ name: p.name, sex: p.sex || '', age: p.age || '', phone: p.phone || '',
      email: p.email || '', address: p.address || '', city: p.city || '',
      state: p.state || '', pincode: p.pincode || '', blood_group: p.blood_group || '', allergies: p.allergies || '' });
    setEditMsg('');
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const res  = await fetch(`${API_URL}/patients/${editPatient.patient_id}`, { method: 'PUT', headers: getAuthHeader(), body: JSON.stringify(editForm) });
      const data = await res.json();
      if (res.ok) { setEditPatient(null); fetchPatients(); }
      else { setEditMsg(data.message || 'Update failed'); }
    } catch { setEditMsg('Network error'); }
    finally { setEditLoading(false); }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res  = await fetch(`${API_URL}/patients/${deleteTarget}`, { method: 'DELETE', headers: getAuthHeader() });
      const data = await res.json();
      if (res.ok) { setDeleteTarget(null); fetchPatients(); }
      else { alert(data.message || 'Delete failed'); }
    } catch { alert('Network error'); }
    finally { setDeleteLoading(false); }
  };

  const filtered = patients.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.patient_id.toLowerCase().includes(search.toLowerCase()) ||
    (p.phone || '').includes(search)
  );

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="page-container" style={{ maxWidth: 1100 }}>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#1e40af' }}>
        <strong>📋 Register Patient vs Book Appointment</strong><br />
        <span style={{ color: '#1e3a8a' }}>
          <b>Register Patient</b> creates a permanent medical record with a unique KY-ID — do this once for every new patient visiting the clinic.<br />
          <b>Appointments</b> (from the website chatbot or enquiry form) are managed separately under the <b>📅 Appointments</b> tab.
        </span>
      </div>

      <h2 style={{ marginBottom: 20 }}>Register New Patient</h2>

      {message === 'success' && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>✅</span>
          <div>
            <strong style={{ color: '#15803d' }}>Patient registered successfully!</strong>
            <div style={{ marginTop: 4 }}>
              Patient ID: <code style={{ background: '#dcfce7', padding: '2px 10px', borderRadius: 4, fontWeight: 700, fontSize: 15, color: '#166534' }}>{lastPatientId}</code>
              <span style={{ color: '#64748b', fontSize: 12, marginLeft: 10 }}>Use this ID for medical records and invoices</span>
            </div>
          </div>
        </div>
      )}
      {message && message !== 'success' && (
        <div className="error-message" style={{ marginBottom: 16 }}>{message}</div>
      )}

      <form onSubmit={handleSubmit} className="patient-form">
        <div className="form-row">
          <div className="form-group">
            <label>Full Name *</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Patient's full name" />
          </div>
          <div className="form-group">
            <label>Sex</label>
            <select name="sex" value={formData.sex} onChange={handleChange}>
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label>Age</label>
            <input type="number" name="age" value={formData.age} onChange={handleChange} placeholder="Age in years" min="0" max="120" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Phone *</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+91 XXXXX XXXXX" />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="patient@email.com" />
          </div>
          <div className="form-group">
            <label>Blood Group</label>
            <select name="blood_group" value={formData.blood_group} onChange={handleChange}>
              <option value="">Select</option>
              {['O+','O-','A+','A-','B+','B-','AB+','AB-'].map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Address</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Street address" />
          </div>
          <div className="form-group">
            <label>City</label>
            <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" />
          </div>
          <div className="form-group">
            <label>Pincode</label>
            <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} placeholder="560060" />
          </div>
        </div>
        <div className="form-group">
          <label>Known Allergies</label>
          <textarea name="allergies" value={formData.allergies} onChange={handleChange} placeholder="List any known allergies or leave blank" rows="2" />
        </div>
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Registering…' : '✅ Register Patient'}
        </button>
      </form>

      <div style={{ marginTop: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <h3 style={{ margin: 0 }}>All Registered Patients ({patients.length})</h3>
          <input type="text" placeholder="Search by name, ID or phone…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, width: 260, color: '#1a1a1a', background: '#ffffff' }} />
        </div>
        {listLoading ? <p style={{ color: '#64748b' }}>Loading patients…</p> : filtered.length === 0 ? (
          <p style={{ color: '#64748b' }}>{search ? 'No patients match your search.' : 'No patients registered yet.'}</p>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  {['Patient ID','Name','Age / Sex','Phone','Email','Blood Grp','Registered', ...(user.role === 'admin' ? ['Actions'] : [])].map(h => (
                    <th key={h} style={{ padding: '9px 12px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', fontSize: 13 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '9px 12px' }}><code style={{ background: '#eff6ff', padding: '2px 8px', borderRadius: 4, fontWeight: 700, fontSize: 12, color: '#1d4ed8' }}>{p.patient_id}</code></td>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{p.name}</td>
                    <td style={{ padding: '9px 12px', color: '#475569', fontSize: 13 }}>{p.age ? `${p.age}y` : '—'}{p.sex ? ` / ${p.sex}` : ''}</td>
                    <td style={{ padding: '9px 12px' }}><a href={`tel:${p.phone}`} style={{ color: '#0891b2', textDecoration: 'none' }}>{p.phone}</a></td>
                    <td style={{ padding: '9px 12px', color: '#475569', fontSize: 13 }}>{p.email || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#475569', fontSize: 13 }}>{p.blood_group || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 12 }}>{fmtDate(p.created_at)}</td>
                    {user.role === 'admin' && (
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => openEdit(p)} className="btn" style={{ fontSize: 12, padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>✏️ Edit</button>
                          <button onClick={() => setDeleteTarget(p.patient_id)} className="btn" style={{ fontSize: 12, padding: '4px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>🗑️ Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Edit Patient Modal ── */}
      {editPatient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '2rem', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h3 style={{ margin: 0 }}>✏️ Edit Patient — {editPatient.patient_id}</h3>
              <button onClick={() => setEditPatient(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            {editMsg && <div className="error-message" style={{ marginBottom: 12 }}>{editMsg}</div>}
            <form onSubmit={handleEditSave}>
              <div className="form-row">
                <div className="form-group"><label>Full Name *</label><input type="text" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} required /></div>
                <div className="form-group"><label>Sex</label>
                  <select value={editForm.sex} onChange={e => setEditForm(p => ({ ...p, sex: e.target.value }))}>
                    <option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group"><label>Age</label><input type="number" value={editForm.age} onChange={e => setEditForm(p => ({ ...p, age: e.target.value }))} min="0" max="120" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Phone *</label><input type="tel" value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} required /></div>
                <div className="form-group"><label>Email</label><input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} /></div>
                <div className="form-group"><label>Blood Group</label>
                  <select value={editForm.blood_group} onChange={e => setEditForm(p => ({ ...p, blood_group: e.target.value }))}>
                    <option value="">Select</option>{['O+','O-','A+','A-','B+','B-','AB+','AB-'].map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Address</label><input type="text" value={editForm.address} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} /></div>
                <div className="form-group"><label>City</label><input type="text" value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} /></div>
                <div className="form-group"><label>Pincode</label><input type="text" value={editForm.pincode} onChange={e => setEditForm(p => ({ ...p, pincode: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Known Allergies</label><textarea value={editForm.allergies} onChange={e => setEditForm(p => ({ ...p, allergies: e.target.value }))} rows="2" /></div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button type="submit" disabled={editLoading} className="btn btn-primary" style={{ flex: 1 }}>{editLoading ? 'Saving…' : '💾 Save Changes'}</button>
                <button type="button" onClick={() => setEditPatient(null)} className="btn" style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '2rem', width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ marginBottom: 8 }}>Delete Patient?</h3>
            <p style={{ color: '#475569', marginBottom: 20, fontSize: 14 }}>
              This will permanently delete <strong>{deleteTarget}</strong> and all their medical records, prescriptions, and invoices. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteTarget(null)} className="btn" style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleteLoading} className="btn"
                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none' }}>
                {deleteLoading ? 'Deleting…' : '🗑️ Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== MEDICAL FORM COMPONENT ====================
export function MedicalForm({ user }) {
  const emptyMedForm = {
    chief_complaint: '', previous_medical_history: '', family_history: '',
    current_medications: '', examination_findings: '', diagnosis: '',
    treatment_notes: '', is_follow_up: false,
  };
  const [patients, setPatients]               = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [selectedDbId, setSelectedDbId]       = useState('');
  const [selectedInfo, setSelectedInfo]       = useState(null);
  const [patientAge, setPatientAge]           = useState('');
  const [patientSex, setPatientSex]           = useState('');

  const [formData, setFormData]   = useState(emptyMedForm);
  const [loading, setLoading]     = useState(false);
  const [message, setMessage]     = useState({ text: '', type: '' });

  // Previous records — shown when patient is selected for follow-up awareness
  const [prevRecords, setPrevRecords]               = useState([]);
  const [prevRecordsLoading, setPrevRecordsLoading] = useState(false);

  // Therapy / service picker
  const [services, setServices]                     = useState([]);
  const [selectedTherapies, setSelectedTherapies]   = useState([]); // [{id, service_name}]
  const [therapyPickValue, setTherapyPickValue]     = useState('');
  const [therapyNotes, setTherapyNotes]             = useState('');

  // After save
  const [savedRecord, setSavedRecord] = useState(null);
  const [savedRx, setSavedRx]         = useState(null);

  useEffect(() => { fetchPatients(); fetchServices(); }, []);

  const fetchPatients = async () => {
    setPatientsLoading(true);
    try {
      const res = await fetch(`${API_URL}/patients`, { headers: getAuthHeader() });
      const data = await res.json();
      setPatients(data.patients || []);
    } catch (e) { console.error('Failed to load patients', e); }
    finally { setPatientsLoading(false); }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_URL}/services`, { headers: getAuthHeader() });
      const d = await res.json();
      setServices(d.services || []);
    } catch (e) { console.error('Failed to load services', e); }
  };

  const addTherapy = (svcId) => {
    if (!svcId) return;
    const svc = services.find(s => String(s.id) === String(svcId));
    if (!svc) return;
    if (selectedTherapies.find(t => t.id === svc.id)) return; // no duplicates
    setSelectedTherapies(prev => [...prev, { id: svc.id, service_name: svc.service_name }]);
    setTherapyPickValue('');
  };

  const removeTherapy = (id) => setSelectedTherapies(prev => prev.filter(t => t.id !== id));

  const fetchPrevRecords = async (dbId) => {
    if (!dbId) { setPrevRecords([]); return; }
    setPrevRecordsLoading(true);
    try {
      const res = await fetch(`${API_URL}/medical-records/patient/${dbId}`, { headers: getAuthHeader() });
      const d = await res.json();
      const recs = d.records || [];
      setPrevRecords(recs);
      // Auto-mark as follow-up if patient has been seen before
      setFormData(prev => ({ ...prev, is_follow_up: recs.length > 0 }));
    } catch (e) { console.error(e); setPrevRecords([]); }
    finally { setPrevRecordsLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 6000);
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDbId) { showMsg('Please select a patient.', 'error'); return; }
    setLoading(true);
    try {
      // 1. Save case sheet (medical record)
      const res = await fetch(`${API_URL}/medical-records`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ patient_id: parseInt(selectedDbId), ...formData })
      });
      const data = await res.json();
      if (!res.ok) {
        showMsg(data.message || 'Failed to save case sheet.', 'error');
        return;
      }

      // Update patient age/sex if provided
      const ageVal = patientAge ? parseInt(patientAge) : null;
      const sexVal = patientSex || null;
      if (ageVal || sexVal) {
        const updates = {};
        if (ageVal) updates.age = ageVal;
        if (sexVal) updates.sex = sexVal;
        try {
          await fetch(`${API_URL}/patients/${selectedDbId}`, {
            method: 'PUT',
            headers: getAuthHeader(),
            body: JSON.stringify(updates)
          });
          setSelectedInfo(prev => prev ? { ...prev, ...updates } : prev);
        } catch (_) {}
      }

      const savedRec = data.record;
      setSavedRecord(savedRec);

      // 2. Auto-create prescription from selected therapies + custom notes
      const therapyServiceNames = selectedTherapies.map(t => t.service_name);
      const therapyNoteLines    = therapyNotes.split('\n').map(s => s.trim()).filter(Boolean);
      const allTherapies        = [...therapyServiceNames, ...therapyNoteLines];
      const rxRes = await fetch(`${API_URL}/prescriptions`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({
          patient_id: savedRec.patient_id,
          medical_record_id: savedRec.id,
          panchakarma_therapies: allTherapies,
          oral_medications: [],
        })
      });
      const rxData = await rxRes.json();
      if (rxRes.ok) {
        setSavedRx({
          ...rxData.prescription,
          diagnosis: formData.diagnosis,
          chief_complaint: formData.chief_complaint,
          treatment_notes: formData.treatment_notes,
        });
        showMsg(`✅ Case sheet & prescription saved for ${selectedInfo?.name || 'patient'}!`);
      } else {
        // Case sheet saved — prescription failed; still show success but note the error
        setSavedRx({ prescription_id: null, patient_id: savedRec.patient_id });
        showMsg(`Case sheet saved. Prescription error: ${rxData.message || 'try again from Prescriptions tab'}`, 'error');
      }
      setFormData(emptyMedForm);
    } catch (err) {
      showMsg('Network error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintRx = () => {
    if (!savedRx) return;
    printPrescription(savedRx, selectedInfo);
  };

  const handleStartNew = () => {
    setSavedRecord(null);
    setSavedRx(null);
    setSelectedDbId('');
    setSelectedInfo(null);
    setPatientAge('');
    setPatientSex('');
    setFormData(emptyMedForm);
    setPrevRecords([]);
    setSelectedTherapies([]);
    setTherapyPickValue('');
    setTherapyNotes('');
  };

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>
      <h2 style={{ marginBottom: 6 }}>Case Sheet &amp; Prescription</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>
        Select a patient → fill clinical details → save. Prescription is generated automatically.
      </p>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 20 }}>
          {message.text}
        </div>
      )}

      {/* ── Case Sheet Form ── */}
      {!savedRx && (
        <form onSubmit={handleSubmit} className="medical-form">
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', color: '#1e293b', fontSize: 16 }}>Select Patient</h3>

            {patientsLoading ? (
              <p style={{ color: '#64748b', fontSize: 13 }}>Loading patient list…</p>
            ) : patients.length === 0 ? (
              <p style={{ color: '#dc2626', fontSize: 13 }}>No registered patients. Please register a patient first.</p>
            ) : (
              <>
                <PatientSelect
                  patients={patients}
                  value={selectedDbId}
                  onChange={(dbId, pat) => {
                    setSelectedDbId(dbId);
                    setSelectedInfo(pat);
                    setPatientAge(pat?.age || '');
                    setPatientSex(pat?.sex || '');
                    fetchPrevRecords(dbId);
                  }}
                  required
                  label="Select Patient *"
                />

                {/* Previous visit summary — shown once records are fetched */}
                {selectedDbId && prevRecordsLoading && (
                  <p style={{ fontSize: 13, color: '#64748b', marginTop: 10 }}>⏳ Loading visit history…</p>
                )}
                {selectedDbId && !prevRecordsLoading && prevRecords.length > 0 && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 16px', marginTop: 12 }}>
                    <strong style={{ color: '#92400e', fontSize: 13 }}>🔄 Returning Patient</strong>
                    <span style={{ color: '#78350f', fontSize: 13 }}> — {prevRecords.length} previous visit{prevRecords.length !== 1 ? 's' : ''} on record</span>
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {prevRecords.slice(0, 3).map((r) => (
                        <div key={r.id} style={{ fontSize: 12, color: '#92400e', paddingLeft: 8, borderLeft: '3px solid #fcd34d' }}>
                          <strong>{fmtDate(r.visit_date)}</strong>
                          {r.is_follow_up ? ' (Follow-up)' : ' (Initial)'} — {r.diagnosis}
                        </div>
                      ))}
                      {prevRecords.length > 3 && (
                        <div style={{ fontSize: 12, color: '#92400e', fontStyle: 'italic' }}>
                          + {prevRecords.length - 3} more — see Patient History tab for full details
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {selectedDbId && !prevRecordsLoading && prevRecords.length === 0 && (
                  <div style={{ fontSize: 13, color: '#15803d', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '8px 12px', marginTop: 10 }}>
                    ✨ First visit — no previous records for this patient.
                  </div>
                )}

                {selectedDbId && (
                  <div className="form-row" style={{ marginTop: 12 }}>
                    <div className="form-group">
                      <label>Patient Age (years) <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}>— update if needed</span></label>
                      <input type="number" value={patientAge} onChange={e => setPatientAge(e.target.value)}
                        min="0" max="120" placeholder="e.g. 35" />
                    </div>
                    <div className="form-group">
                      <label>Patient Sex <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}>— update if needed</span></label>
                      <select value={patientSex} onChange={e => setPatientSex(e.target.value)}>
                        <option value="">— Not specified —</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label>Chief Complaint *</label>
            <textarea name="chief_complaint" value={formData.chief_complaint} onChange={handleChange}
              placeholder="Main reason for today's visit" rows="3" required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Previous Medical History</label>
              <textarea name="previous_medical_history" value={formData.previous_medical_history} onChange={handleChange}
                placeholder="Past illnesses, surgeries, hospitalizations" rows="3" />
            </div>
            <div className="form-group">
              <label>Family History</label>
              <textarea name="family_history" value={formData.family_history} onChange={handleChange}
                placeholder="Hereditary conditions in family" rows="3" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Current Medications</label>
              <textarea name="current_medications" value={formData.current_medications} onChange={handleChange}
                placeholder="Ongoing medications, supplements" rows="3" />
            </div>
            <div className="form-group">
              <label>Examination Findings</label>
              <textarea name="examination_findings" value={formData.examination_findings} onChange={handleChange}
                placeholder="Pulse (Nadi), tongue, eyes, physical exam" rows="3" />
            </div>
          </div>

          <div className="form-group">
            <label>Diagnosis *</label>
            <textarea name="diagnosis" value={formData.diagnosis} onChange={handleChange}
              placeholder="Ayurvedic and/or clinical diagnosis" rows="3" required />
          </div>

          {/* ── Panchakarma / Therapy Recommendations ── */}
          <div className="form-group" style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '14px 16px' }}>
            <label style={{ color: '#15803d', fontWeight: 700, fontSize: 14 }}>
              💆 Panchakarma / Therapy Recommendations
              <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12, marginLeft: 6 }}>— optional; selected items appear on the prescription</span>
            </label>

            {/* Service dropdown picker */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
              <select
                value={therapyPickValue}
                onChange={e => { addTherapy(e.target.value); }}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #86efac', borderRadius: 6, fontSize: 14, color: '#1a1a1a', background: '#fff' }}
              >
                <option value="">+ Pick a therapy / service…</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>{s.service_name}</option>
                ))}
              </select>
            </div>

            {/* Selected therapy chips */}
            {selectedTherapies.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {selectedTherapies.map(t => (
                  <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                    borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 500 }}>
                    {t.service_name}
                    <button type="button" onClick={() => removeTherapy(t.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}

            {/* Custom notes */}
            <textarea
              value={therapyNotes}
              onChange={e => setTherapyNotes(e.target.value)}
              placeholder={"Additional custom therapy notes (optional)…\ne.g. Abhyanga with Dhanwantharam oil – 45 mins daily for 7 days"}
              rows="3"
              style={{ marginTop: 10, width: '100%', padding: '8px 12px', border: '1px solid #86efac', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff', resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label>
              Treatment Notes / General Instructions
              <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12, marginLeft: 6 }}>— internal notes, not on prescription</span>
            </label>
            <textarea name="treatment_notes" value={formData.treatment_notes} onChange={handleChange}
              placeholder="Lifestyle guidance, diet notes, follow-up plan, any other observations…"
              rows="4" />
          </div>

          <div className="form-group checkbox" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" name="is_follow_up" id="is_follow_up" checked={formData.is_follow_up} onChange={handleChange} />
            <label htmlFor="is_follow_up" style={{ fontWeight: 400, cursor: 'pointer' }}>This is a follow-up visit</label>
          </div>

          <button type="submit" disabled={loading || !selectedDbId} className="btn btn-primary" style={{ marginTop: 8 }}>
            {loading ? 'Saving…' : '💾 Save Case Sheet & Generate Prescription'}
          </button>
        </form>
      )}

      {/* ── Success: Print ── */}
      {savedRx && (
        <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 10, padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
          <h3 style={{ color: '#15803d', marginBottom: 6 }}>Case Sheet &amp; Prescription Saved!</h3>
          <p style={{ color: '#166534', marginBottom: 4, fontSize: 14 }}>
            Patient: <strong>{selectedInfo?.name}</strong>
          </p>
          {savedRx.prescription_id && (
            <p style={{ color: '#166534', marginBottom: 16, fontSize: 13 }}>
              Prescription ID: <strong>{savedRx.prescription_id}</strong>
            </p>
          )}
          <p style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>
            The prescription is also visible in the Prescriptions tab.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {savedRx.prescription_id && (
              <button className="btn btn-primary" onClick={handlePrintRx}>
                🖨️ Print Prescription
              </button>
            )}
            <button className="btn" onClick={handleStartNew}>
              ➕ New Consultation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== PRESCRIPTION VIEW COMPONENT ====================
export function PrescriptionView({ user }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [search, setSearch]               = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [rxRes, patRes] = await Promise.all([
        fetch(`${API_URL}/prescriptions`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/patients`,      { headers: getAuthHeader() }),
      ]);
      if (!rxRes.ok) throw new Error(`HTTP ${rxRes.status}`);
      const rxData  = await rxRes.json();
      const patData = await patRes.json();
      setPrescriptions(rxData.prescriptions || []);
      setPatients(patData.patients || []);
    } catch (err) {
      setError('Failed to load prescriptions. Please try again.');
      console.error('Error fetching prescriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const filtered = prescriptions.filter(p =>
    !search ||
    (p.prescription_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.patient_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handlePrint = (rx) => {
    const patient = patients.find(p => p.id === rx.patient_id) || null;
    printPrescription(rx, patient);
  };

  return (
    <div className="page-container" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0 }}>💊 Prescriptions</h2>
          {user?.role === 'receptionist' && (
            <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
              View and print prescriptions generated by doctors.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="text" placeholder="Search by ID or patient name…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minWidth: 220, color: '#1a1a1a', background: '#fff' }} />
          <button className="btn" style={{ fontSize: 13 }} onClick={fetchData}>🔄 Refresh</button>
        </div>
      </div>

      {error && <div className="message error" style={{ marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading prescriptions…</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💊</div>
          <p style={{ fontSize: 15 }}>{search ? 'No matching prescriptions.' : 'No prescriptions yet.'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Rx ID', 'Patient', 'Doctor', 'Date', 'Duration', 'Medications', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', fontWeight: 600, textAlign: 'left', fontSize: 13 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((rx, i) => {
                const meds = Array.isArray(rx.oral_medications)
                  ? rx.oral_medications.join(', ')
                  : (rx.oral_medications || '');
                return (
                  <tr key={rx.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 13, color: '#4f46e5' }}>{rx.prescription_id}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{rx.patient_name || `ID: ${rx.patient_id}`}</td>
                    <td style={{ padding: '9px 12px', color: '#475569', fontSize: 13 }}>{rx.doctor_name || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(rx.created_at)}</td>
                    <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13 }}>{rx.duration_days ? `${rx.duration_days} days` : '—'}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={meds}>
                      {meds || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => handlePrint(rx)}>
                        🖨️ Print / PDF
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ padding: '8px 12px', color: '#94a3b8', fontSize: 12 }}>
            {filtered.length} prescription{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}


// ==================== INVOICE GENERATOR COMPONENT ====================
export function InvoiceGenerator({ user }) {
  const [patients, setPatients]           = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [selectedPatientDbId, setSelectedPatientDbId] = useState('');
  const [selectedPatient, setSelectedPatient]         = useState(null);

  const [invoiceItems, setInvoiceItems]   = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [quantity, setQuantity]           = useState(1);
  const [services, setServices]           = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes]                 = useState('');

  const [discountPercent, setDiscountPercent] = useState(0);

  const [creating, setCreating]           = useState(false);
  const [message, setMessage]             = useState({ text: '', type: '' });
  const [lastInvoice, setLastInvoice]     = useState(null);

  // Invoice list
  const [invoices, setInvoices]           = useState([]);
  const [invLoading, setInvLoading]       = useState(true);
  const [updatingInvId, setUpdatingInvId] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchPatients();
    fetchInvoices();
  }, []);

  const fetchServices = async () => {
    try {
      const res  = await fetch(`${API_URL}/services`, { headers: getAuthHeader() });
      const data = await res.json();
      setServices(data.services || []);
    } catch (e) { console.error('Error fetching services', e); }
  };

  const fetchPatients = async () => {
    setPatientsLoading(true);
    try {
      const res  = await fetch(`${API_URL}/patients`, { headers: getAuthHeader() });
      const data = await res.json();
      setPatients(data.patients || []);
    } catch (e) { console.error('Error fetching patients', e); }
    finally { setPatientsLoading(false); }
  };

  const fetchInvoices = async () => {
    setInvLoading(true);
    try {
      const res  = await fetch(`${API_URL}/invoices`, { headers: getAuthHeader() });
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (e) { console.error('Error fetching invoices', e); }
    finally { setInvLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const addItem = () => {
    if (!selectedService || quantity < 1) return;
    const service = services.find(s => s.id === parseInt(selectedService));
    if (!service) return;
    setInvoiceItems(prev => [...prev, {
      service_id: service.id,
      description: service.service_name,
      quantity: parseInt(quantity),
      unit_price: service.price,
      total_price: service.price * parseInt(quantity)
    }]);
    setSelectedService('');
    setQuantity(1);
  };

  const removeItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItemPrice = (index, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    setInvoiceItems(prev => prev.map((item, i) =>
      i === index ? { ...item, unit_price: price, total_price: price * item.quantity } : item
    ));
  };

  const handleCreateInvoice = async () => {
    if (!selectedPatientDbId || invoiceItems.length === 0) {
      showMsg('Please select a patient and add at least one item.', 'error');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/invoices`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({
          patient_id: parseInt(selectedPatientDbId),
          items: invoiceItems,
          payment_method: paymentMethod || null,
          notes: notes || null,
          discount_percent: parseFloat(discountPercent) || 0,
        })
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`Invoice ${data.invoice.invoice_id} created successfully!`);
        setLastInvoice(data.invoice);
        setSelectedPatientDbId('');
        setSelectedPatient(null);
        setInvoiceItems([]);
        setPaymentMethod('');
        setNotes('');
        setDiscountPercent(0);
        fetchInvoices();
      } else {
        showMsg(data.message || 'Error creating invoice', 'error');
      }
    } catch (e) {
      showMsg('Network error. Please try again.', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdatePaymentStatus = async (invoiceId, status) => {
    setUpdatingInvId(invoiceId);
    try {
      const res = await fetch(`${API_URL}/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ payment_status: status })
      });
      if (res.ok) {
        fetchInvoices();
      }
    } catch (e) { console.error(e); }
    finally { setUpdatingInvId(null); }
  };

  const sendOnWhatsApp = (inv) => {
    const items = (inv.items || []).map(i => `  • ${i.service_name || i.description} × ${i.quantity} = ₹${Number(i.total_price).toFixed(2)}`).join('\n');
    const text = encodeURIComponent(
      `KayaSparsha Ayurvedic Clinic — Invoice Summary\n\n` +
      `Invoice: ${inv.invoice_id}\nPatient: ${inv.patient_name || '—'}\nDate: ${new Date(inv.issued_date || inv.created_at).toLocaleDateString('en-IN')}\n\n` +
      `Services:\n${items}\n\nTotal: ₹${Number(inv.total_amount).toFixed(2)}\nStatus: ${(inv.payment_status || 'pending').toUpperCase()}\n\nThank you for choosing KayaSparsha! 🌿`
    );
    const phone = inv.patient_phone ? inv.patient_phone.replace(/\D/g, '').replace(/^0/, '91') : WA_NUMBER;
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const subtotal  = invoiceItems.reduce((sum, item) => sum + item.total_price, 0);
  const discount  = subtotal * (parseFloat(discountPercent) || 0) / 100;
  const total     = subtotal - discount;

  const STATUS_BADGE = { paid: { bg: '#dcfce7', color: '#15803d' }, pending: { bg: '#fef9c3', color: '#92400e' }, partial: { bg: '#dbeafe', color: '#1d4ed8' } };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="page-container" style={{ maxWidth: 1100 }}>
      <h2 style={{ marginBottom: 6 }}>Invoice Generator</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>Create invoices for clinic patients, print PDF, and send via WhatsApp.</p>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {/* Last created invoice actions */}
      {lastInvoice && (
        <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 10, padding: '18px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <strong style={{ color: '#15803d' }}>Invoice created: {lastInvoice.invoice_id}</strong>
            <div style={{ color: '#166534', fontSize: 13 }}>Total: ₹{Number(lastInvoice.total_amount).toFixed(2)} | Status: {lastInvoice.payment_status}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => printInvoice(lastInvoice)}>🖨️ Print / PDF</button>
            <button className="btn" style={{ background: '#25D366', color: '#fff', border: 'none' }} onClick={() => sendOnWhatsApp(lastInvoice)}>
              💬 Send on WhatsApp
            </button>
            <button className="btn" style={{ fontSize: 12 }} onClick={() => setLastInvoice(null)}>✕ Dismiss</button>
          </div>
        </div>
      )}

      <div className="invoice-form">
        {/* Patient Selection */}
        <div className="form-group" style={{ marginBottom: 20 }}>
          {patientsLoading ? (
            <p style={{ color: '#64748b' }}>Loading patients…</p>
          ) : (
            <PatientSelect
              patients={patients}
              value={selectedPatientDbId}
              onChange={(dbId, pat) => { setSelectedPatientDbId(dbId); setSelectedPatient(pat); }}
              required
              label="Select Patient *"
            />
          )}
        </div>

        {/* Service Selector */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>Add Services</h4>
          <div className="service-selector">
            <div className="form-row" style={{ alignItems: 'flex-end', gap: 12 }}>
              <div className="form-group" style={{ flex: 2 }}>
                <label>Select Service</label>
                <select value={selectedService} onChange={e => setSelectedService(e.target.value)}
                  style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, width: '100%', color: '#1a1a1a', background: '#fff' }}>
                  <option value="">Choose a service</option>
                  {services.map(s => (
                    <option key={s.id} value={s.id}>{s.service_name} (₹{s.price})</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Qty</label>
                <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} min="1"
                  style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, width: '100%' }} />
              </div>
              <button onClick={addItem} className="btn btn-primary" style={{ height: 40, whiteSpace: 'nowrap' }}>
                ➕ Add
              </button>
            </div>
          </div>
        </div>

        {/* Invoice Items Table */}
        {invoiceItems.length > 0 && (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, fontSize: 13 }}>Service</th>
                  <th style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 600, fontSize: 13 }}>Qty</th>
                  <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, fontSize: 13 }}>Unit Price</th>
                  <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, fontSize: 13 }}>Total</th>
                  <th style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 600, fontSize: 13 }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {invoiceItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{item.description}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>{item.quantity}</td>
                    <td style={{ padding: '9px 6px', textAlign: 'right' }}>
                      <input
                        type="number"
                        value={item.unit_price}
                        min="0"
                        step="0.01"
                        onChange={e => updateItemPrice(idx, e.target.value)}
                        style={{ width: 90, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>₹{Number(item.total_price).toFixed(2)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                      <button onClick={() => removeItem(idx)} style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 13 }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Totals + Options */}
        {invoiceItems.length > 0 && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                    style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, width: '100%', color: '#1a1a1a', background: '#fff' }}>
                    <option value="">Not specified</option>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI / QR</option>
                    <option value="card">Card</option>
                    <option value="netbanking">Net Banking</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes…"
                    style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, width: '100%' }} />
                </div>
              </div>
              <div style={{ minWidth: 240 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                  <span>Subtotal:</span><span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 14, color: '#64748b' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Discount
                    <input
                      type="number"
                      value={discountPercent}
                      min="0"
                      max="100"
                      step="0.5"
                      onChange={e => setDiscountPercent(e.target.value)}
                      style={{ width: 54, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, textAlign: 'right' }}
                    />
                    %:
                  </span>
                  <span style={{ color: discount > 0 ? '#b91c1c' : '#64748b' }}>
                    {discount > 0 ? `−₹${discount.toFixed(2)}` : '₹0.00'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontSize: 18, fontWeight: 700, borderTop: '2px solid #1a3a2a', color: '#1a3a2a' }}>
                  <span>Total:</span><span>₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <button onClick={handleCreateInvoice} disabled={creating || !selectedPatientDbId} className="btn btn-primary btn-large" style={{ marginTop: 16, fontSize: 15, padding: '12px 32px' }}>
              {creating ? 'Creating…' : '✅ Create Invoice'}
            </button>
          </div>
        )}
      </div>

      {/* Existing Invoices List */}
      <div style={{ marginTop: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>All Invoices ({invoices.length})</h3>
          <button className="btn" style={{ fontSize: 13 }} onClick={fetchInvoices}>🔄 Refresh</button>
        </div>

        {invLoading ? (
          <p style={{ color: '#64748b' }}>Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p style={{ color: '#64748b' }}>No invoices yet.</p>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Invoice ID', 'Patient', 'Date', 'Total', 'Payment Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', fontWeight: 600, textAlign: 'left', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => {
                  const sb = STATUS_BADGE[inv.payment_status] || { bg: '#f1f5f9', color: '#475569' };
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 13, color: '#4f46e5' }}>{inv.invoice_id}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 500 }}>{inv.patient_name || `ID: ${inv.patient_id}`}</td>
                      <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13 }}>{fmtDate(inv.issued_date || inv.created_at)}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 600 }}>₹{Number(inv.total_amount).toFixed(2)}</td>
                      <td style={{ padding: '9px 12px' }}>
                        <select
                          value={inv.payment_status}
                          onChange={e => handleUpdatePaymentStatus(inv.invoice_id, e.target.value)}
                          disabled={updatingInvId === inv.invoice_id}
                          style={{ background: sb.bg, color: sb.color, border: `1px solid ${sb.color}40`, borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="partial">Partial</option>
                        </select>
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => printInvoice(inv)}>
                            🖨️ Print
                          </button>
                          <button className="btn" style={{ fontSize: 12, padding: '4px 10px', background: '#25D366', color: '#fff', border: 'none' }} onClick={() => sendOnWhatsApp(inv)}>
                            💬 WA
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== SERVICE MANAGEMENT COMPONENT ====================
export function ServiceManagement() {
  const [services, setServices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [message, setMessage]       = useState({ text: '', type: '' });
  const [editingId, setEditingId]   = useState(null);
  const [editData, setEditData]     = useState({});

  const emptyForm = { service_name: '', category: 'other', price: '', description: '' };
  const [formData, setFormData]     = useState(emptyForm);
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/services?all=true`, { headers: getAuthHeader() });
      const data = await res.json();
      setServices(data.services || []);
    } catch (e) { console.error('Error fetching services', e); }
    finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      const res = await fetch(`${API_URL}/services`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`Service "${formData.service_name}" added!`);
        setFormData(emptyForm);
        fetchServices();
      } else {
        showMsg(data.message || 'Failed to add service', 'error');
      }
    } catch (e) { showMsg('Network error', 'error'); }
    finally { setAddLoading(false); }
  };

  const handleUpdateService = async (serviceId) => {
    try {
      const res = await fetch(`${API_URL}/services/${serviceId}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify(editData)
      });
      const data = await res.json();
      if (res.ok) {
        showMsg('Service updated!');
        setEditingId(null);
        fetchServices();
      } else {
        showMsg(data.message || 'Update failed', 'error');
      }
    } catch (e) { showMsg('Network error', 'error'); }
  };

  const handleToggleActive = async (service) => {
    try {
      const res = await fetch(`${API_URL}/services/${service.id}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ is_active: !service.is_active })
      });
      if (res.ok) {
        showMsg(`"${service.service_name}" ${!service.is_active ? 'activated' : 'deactivated'}`);
        fetchServices();
      }
    } catch (e) { showMsg('Network error', 'error'); }
  };

  const startEdit = (service) => {
    setEditingId(service.id);
    setEditData({
      service_name: service.service_name,
      category: service.category,
      price: service.price,
      description: service.description || ''
    });
  };

  const CATS = { consultation: '🩺 Consultation', panchakarma: '🌿 Panchakarma', other: '⚙️ Other' };

  return (
    <div className="page-container" style={{ maxWidth: 1100 }}>
      <h2 style={{ marginBottom: 6 }}>Manage Services &amp; Pricing</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>Add, edit, and activate/deactivate clinic services used in invoices.</p>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 24, alignItems: 'flex-start' }}>
        {/* Add Service Form */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b' }}>➕ Add New Service</h3>
          <form onSubmit={handleAddService}>
            <div className="form-group">
              <label>Service Name *</label>
              <input type="text" name="service_name" value={formData.service_name}
                onChange={e => setFormData(p => ({ ...p, service_name: e.target.value }))}
                required placeholder="e.g., Abhyanga" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select name="category" value={formData.category}
                onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}>
                <option value="consultation">Consultation</option>
                <option value="panchakarma">Panchakarma</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Price (₹) *</label>
              <input type="number" name="price" value={formData.price}
                onChange={e => setFormData(p => ({ ...p, price: e.target.value }))}
                required placeholder="Service price" min="0" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea name="description" value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Short description (optional)" rows="3" />
            </div>
            <button type="submit" disabled={addLoading} className="btn btn-primary" style={{ width: '100%' }}>
              {addLoading ? 'Adding…' : '✅ Add Service'}
            </button>
          </form>
        </div>

        {/* Services List */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>All Services ({services.length})</h3>
            <button className="btn" style={{ fontSize: 13 }} onClick={fetchServices}>🔄 Refresh</button>
          </div>
          {loading ? (
            <p style={{ color: '#64748b' }}>Loading…</p>
          ) : services.length === 0 ? (
            <p style={{ color: '#64748b' }}>No services yet. Add one using the form.</p>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Service Name', 'Category', 'Price', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', fontWeight: 600, textAlign: 'left', fontSize: 13 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc, i) => (
                    <React.Fragment key={svc.id}>
                      <tr style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa', opacity: svc.is_active ? 1 : 0.6 }}>
                        {editingId === svc.id ? (
                          <>
                            <td style={{ padding: '8px 12px' }}>
                              <input value={editData.service_name} onChange={e => setEditData(p => ({ ...p, service_name: e.target.value }))}
                                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, width: '100%' }} />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <select value={editData.category} onChange={e => setEditData(p => ({ ...p, category: e.target.value }))}
                                style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, color: '#1a1a1a', background: '#fff' }}>
                                <option value="consultation">Consultation</option>
                                <option value="panchakarma">Panchakarma</option>
                                <option value="other">Other</option>
                              </select>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input type="number" value={editData.price} onChange={e => setEditData(p => ({ ...p, price: e.target.value }))}
                                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, width: 90 }} min="0" />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{ fontSize: 12, color: '#64748b' }}>Editing…</span>
                            </td>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => handleUpdateService(svc.id)} className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }}>
                                  💾 Save
                                </button>
                                <button onClick={() => setEditingId(null)} className="btn" style={{ fontSize: 12, padding: '4px 10px' }}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: '9px 12px', fontWeight: 500 }}>
                              {svc.service_name}
                              {svc.description && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{svc.description}</div>}
                            </td>
                            <td style={{ padding: '9px 12px', color: '#475569', fontSize: 13 }}>{CATS[svc.category] || svc.category}</td>
                            <td style={{ padding: '9px 12px', fontWeight: 600 }}>₹{Number(svc.price).toFixed(2)}</td>
                            <td style={{ padding: '9px 12px' }}>
                              <span style={{
                                background: svc.is_active ? '#dcfce7' : '#f1f5f9',
                                color: svc.is_active ? '#15803d' : '#64748b',
                                padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600
                              }}>
                                {svc.is_active ? '✓ Active' : '✗ Inactive'}
                              </span>
                            </td>
                            <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => startEdit(svc)} className="btn" style={{ fontSize: 12, padding: '4px 10px' }}>
                                  ✏️ Edit
                                </button>
                                <button onClick={() => handleToggleActive(svc)} className="btn"
                                  style={{ fontSize: 12, padding: '4px 10px', background: svc.is_active ? '#fef2f2' : '#f0fdf4', color: svc.is_active ? '#b91c1c' : '#15803d', border: 'none' }}>
                                  {svc.is_active ? '🚫 Deactivate' : '✅ Activate'}
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================== USER MANAGEMENT COMPONENT (Admin only) ====================
export function UserManagement() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [message, setMessage]     = useState({ text: '', type: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm]     = useState({ name: '', username: '', email: '', phone: '', role: 'receptionist', password: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/users`, { headers: getAuthHeader() });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) { showMsg('Failed to load users', 'error'); }
    finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      const res  = await fetch(`${API_URL}/users`, { method: 'POST', headers: getAuthHeader(), body: JSON.stringify(addForm) });
      const data = await res.json();
      if (res.ok) {
        showMsg(`User "${addForm.name}" created successfully!`);
        setAddForm({ name: '', username: '', email: '', phone: '', role: 'receptionist', password: '' });
        setShowAddForm(false);
        fetchUsers();
      } else {
        showMsg(data.message || 'Failed to create user', 'error');
      }
    } catch (e) { showMsg('Network error', 'error'); }
    finally { setAddLoading(false); }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await fetch(`${API_URL}/users/${user.user_id}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ is_active: !user.is_active })
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`${user.name} ${!user.is_active ? 'activated' : 'deactivated'}`);
        fetchUsers();
      } else {
        showMsg(data.message || 'Update failed', 'error');
      }
    } catch (e) { showMsg('Network error', 'error'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const res = await fetch(`${API_URL}/users/${resetTarget.user_id}/password`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`Password for ${resetTarget.name} reset successfully`);
        setResetTarget(null);
        setNewPassword('');
      } else {
        showMsg(data.message || 'Reset failed', 'error');
      }
    } catch (e) { showMsg('Network error', 'error'); }
    finally { setResetLoading(false); }
  };

  const roleColors = { admin: '#7c3aed', doctor: '#0891b2', receptionist: '#059669' };
  const roleLabel  = { admin: '🛡️ Admin', doctor: '🩺 Doctor', receptionist: '📋 Receptionist' };

  return (
    <div className="page-container" style={{ maxWidth: '1200px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>User Management</h2>
        <button className="btn btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕ Cancel' : '➕ Add User'}
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      {showAddForm && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem', marginBottom: '2rem' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Add New Staff User</h3>
          <form onSubmit={handleAddUser}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Dr. Anil Kumar" required />
              </div>
              <div className="form-group">
                <label>Username <span style={{ color: '#64748b', fontWeight: 400 }}>(for login)</span> *</label>
                <input type="text" value={addForm.username} onChange={e => setAddForm(p => ({ ...p, username: e.target.value.toLowerCase().replace(/\s+/g,'') }))} placeholder="e.g. drsuresh" required autoComplete="off" />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="user@clinic.com" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} placeholder="10-digit mobile" required />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select value={addForm.role} onChange={e => setAddForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="receptionist">Receptionist</option>
                  <option value="doctor">Doctor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Initial Password *</label>
                <input type="password" value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" minLength={6} required />
              </div>
            </div>
            <button type="submit" disabled={addLoading} className="btn btn-primary">
              {addLoading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {loading ? <p>Loading users...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                {['Name','Username','Email','Phone','Role','Status','Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} style={{ borderBottom: '1px solid #e2e8f0', opacity: u.is_active ? 1 : 0.55 }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>{u.name}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {u.username
                      ? <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontSize: 13, fontWeight: 600, color: '#0369a1' }}>{u.username}</code>
                      : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#475569', fontSize: '0.9rem' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px', color: '#475569', fontSize: '0.9rem' }}>{u.phone}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: roleColors[u.role] + '20', color: roleColors[u.role], padding: '2px 10px', borderRadius: '12px', fontSize: '0.82rem', fontWeight: 600 }}>
                      {roleLabel[u.role] || u.role}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: u.is_active ? '#dcfce7' : '#fee2e2', color: u.is_active ? '#16a34a' : '#dc2626', padding: '2px 10px', borderRadius: '12px', fontSize: '0.82rem', fontWeight: 600 }}>
                      {u.is_active ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => { setResetTarget(u); setNewPassword(''); }} className="btn" style={{ fontSize: '0.82rem', padding: '4px 10px' }}>
                        🔑 Reset Password
                      </button>
                      <button onClick={() => handleToggleActive(u)} className="btn"
                        style={{ fontSize: '0.82rem', padding: '4px 10px', background: u.is_active ? '#fee2e2' : '#dcfce7', color: u.is_active ? '#dc2626' : '#16a34a', border: 'none' }}>
                        {u.is_active ? '🚫 Deactivate' : '✅ Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p style={{ textAlign: 'center', color: '#64748b', marginTop: '2rem' }}>No users found.</p>}
        </div>
      )}

      {resetTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '2rem', width: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0 }}>🔑 Reset Password</h3>
            <p style={{ color: '#475569', marginBottom: '1.2rem' }}>Set a new password for <strong>{resetTarget.name}</strong></p>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>New Password *</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" minLength={6} required autoFocus />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                <button type="submit" disabled={resetLoading} className="btn btn-primary" style={{ flex: 1 }}>
                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button type="button" className="btn" style={{ flex: 1 }} onClick={() => { setResetTarget(null); setNewPassword(''); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== APPOINTMENTS PAGE ====================
export function AppointmentsPage({ user, setCurrentPage }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('all');
  const [updatingId, setUpdatingId]     = useState(null);
  const [message, setMessage]           = useState('');

  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');
  const [filterService, setFilterService]   = useState('');

  // Slot bookings
  const todayStr = new Date().toISOString().split('T')[0];
  const [slotDate, setSlotDate]         = useState(todayStr);
  const [slots, setSlots]               = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);

  // Slot reschedule modal
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // slot object
  const [rsDate, setRsDate]                     = useState('');
  const [rsTime, setRsTime]                     = useState('');
  const [rsLoading, setRsLoading]               = useState(false);

  useEffect(() => { fetchAppointments(); }, [activeTab]);
  useEffect(() => { fetchSlots(); }, [slotDate]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const url = activeTab === 'all' ? `${API_URL}/appointments` : `${API_URL}/appointments?status=${activeTab}`;
      const res  = await fetch(url, { headers: getAuthHeader() });
      const data = await res.json();
      setAppointments(data.appointments || []);
    } catch (e) { console.error('Failed to load appointments', e); }
    finally { setLoading(false); }
  };

  const fetchSlots = async () => {
    setSlotsLoading(true);
    try {
      const res  = await fetch(`${API_URL}/slots?date=${slotDate}`, { headers: getAuthHeader() });
      const data = await res.json();
      setSlots((data.slots || []).filter(s => s.status === 'booked'));
    } catch (e) { console.error('Failed to load slots', e); }
    finally { setSlotsLoading(false); }
  };

  const openReschedule = (slot) => {
    setRescheduleTarget(slot);
    setRsDate(slot.date);
    setRsTime(slot.time_start);
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget) return;
    setRsLoading(true);
    try {
      const res = await fetch(`${API_URL}/slots/${rescheduleTarget.id}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ edit: true, date: rsDate, time_start: rsTime }),
      });
      const d = await res.json();
      if (res.ok) {
        showMsg(`Slot rescheduled to ${rsDate} at ${rsTime}`);
        setRescheduleTarget(null);
        fetchSlots();
      } else {
        showMsg(d.message || 'Failed to reschedule.', 'error');
      }
    } catch { showMsg('Network error.', 'error'); }
    finally { setRsLoading(false); }
  };

  const handleReleaseSlot = async (slot) => {
    if (!window.confirm(`Release booking for ${slot.patient_name} at ${slot.time_start}? This frees the slot.`)) return;
    try {
      const res = await fetch(`${API_URL}/slots/${slot.id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      if (res.ok) { showMsg('Slot released successfully.'); fetchSlots(); }
      else { const d = await res.json(); showMsg(d.message || 'Failed to release.', 'error'); }
    } catch { showMsg('Network error.', 'error'); }
  };

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const res  = await fetch(`${API_URL}/appointments/${id}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`Status updated to "${status}"`);
        fetchAppointments();
      } else {
        showMsg(data.message || 'Update failed', 'error');
      }
    } finally { setUpdatingId(null); }
  };

  const registerPatient = async (id) => {
    setUpdatingId(id);
    try {
      const res  = await fetch(`${API_URL}/appointments/${id}/register-patient`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (res.ok) {
        const pid = data.patient && data.patient.patient_id;
        showMsg(data.already_registered
          ? `Already registered — Patient ID ${pid}`
          : `Registered as new patient — Patient ID ${pid}`);
        fetchAppointments();
      } else {
        showMsg(data.message || 'Could not register patient', 'error');
      }
    } finally { setUpdatingId(null); }
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 3500);
  };

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterService('');
  };

  // Client-side filtering
  const filteredAppointments = appointments.filter(a => {
    if (filterService && !(a.service || '').toLowerCase().includes(filterService.toLowerCase())) return false;
    if (filterDateFrom) {
      const apptDate = new Date(a.created_at);
      if (apptDate < new Date(filterDateFrom)) return false;
    }
    if (filterDateTo) {
      const apptDate = new Date(a.created_at);
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59);
      if (apptDate > toDate) return false;
    }
    return true;
  });

  const TABS = [
    { key: 'all',       label: 'All' },
    { key: 'new',       label: '🆕 New' },
    { key: 'contacted', label: '📞 Contacted' },
    { key: 'booked',    label: '✅ Booked' },
    { key: 'cancelled', label: '❌ Cancelled' },
  ];

  const STATUS_STYLE = {
    new:       { background: '#eff6ff', color: '#1d4ed8' },
    contacted: { background: '#fef9c3', color: '#92400e' },
    booked:    { background: '#f0fdf4', color: '#15803d' },
    cancelled: { background: '#fef2f2', color: '#b91c1c' },
  };

  const hasFilters = filterDateFrom || filterDateTo || filterService;

  return (
    <div className="page-container" style={{ maxWidth: 1300 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0 }}>📅 Appointments</h2>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>All enquiries from the website contact form and the booking chatbot.</p>
        </div>
        <button className="btn" style={{ fontSize: 13 }} onClick={fetchAppointments}>🔄 Refresh</button>
      </div>

      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#166534' }}>
        <strong>How it works:</strong> Patients booking via the website or chatbot appear here as <b>New</b>.
        Mark as <b>Contacted</b> once you've called them, then <b>Booked</b> after confirming — then register them as a patient.
      </div>

      {/* ── FILTERS ── */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>From Date</label>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#1a1a1a', background: '#fff' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>To Date</label>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#1a1a1a', background: '#fff' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Service Requested</label>
            <input type="text" value={filterService} onChange={e => setFilterService(e.target.value)}
              placeholder="Filter by service…"
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minWidth: 200, color: '#1a1a1a', background: '#fff' }} />
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="btn" style={{ fontSize: 12, padding: '7px 14px', alignSelf: 'flex-end' }}>
              ✕ Clear Filters
            </button>
          )}
          {hasFilters && (
            <span style={{ alignSelf: 'flex-end', fontSize: 12, color: '#64748b', paddingBottom: 8 }}>
              Showing {filteredAppointments.length} of {appointments.length}
            </span>
          )}
        </div>
      </div>

      {message && message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 14 }}>{message.text}</div>
      )}

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e2e8f0' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding: '9px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontWeight: activeTab === t.key ? 600 : 400, color: activeTab === t.key ? '#1d4ed8' : '#64748b',
              borderBottom: activeTab === t.key ? '2px solid #1d4ed8' : '2px solid transparent', fontSize: 13, marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading appointments…</p>
      ) : filteredAppointments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <p style={{ fontSize: 15 }}>No appointments{hasFilters ? ' matching filters' : ' in this category'}.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#','Name','Phone','Service Requested','Preferred Date/Time','Received','Status','Actions'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', fontWeight: 600, textAlign: 'left', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '9px 12px', color: '#94a3b8', fontSize: 12 }}>{a.id}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 500 }}>{a.name}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <a href={`tel:${a.phone}`} style={{ color: '#0891b2', textDecoration: 'none' }}>{a.phone}</a>
                  </td>
                  <td style={{ padding: '9px 12px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.service}>{a.service || '—'}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: '#475569', fontSize: 13 }}>{a.preferred_date || '—'}</td>
                  <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ ...(STATUS_STYLE[a.status] || {}), padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                      {a.status}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {/* All roles: register enquiry as a patient */}
                      <button onClick={() => registerPatient(a.id)} disabled={updatingId === a.id}
                        className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7' }}
                        title="Register this enquiry as a patient (or look up their Patient ID if already registered)">
                        👤 Register Patient
                      </button>
                      {/* Receptionist / Admin actions */}
                      {(user.role === 'admin' || user.role === 'receptionist') && (
                        <>
                          {a.status === 'new' && (
                            <button onClick={() => updateStatus(a.id, 'contacted')} disabled={updatingId === a.id}
                              className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#fef9c3', color: '#92400e', border: '1px solid #d97706' }}>
                              📞 Contacted
                            </button>
                          )}
                          {(a.status === 'new' || a.status === 'contacted') && (
                            <button onClick={() => updateStatus(a.id, 'booked')} disabled={updatingId === a.id}
                              className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#f0fdf4', color: '#15803d', border: '1px solid #16a34a' }}>
                              ✅ Booked
                            </button>
                          )}
                          {a.status !== 'cancelled' && (
                            <button onClick={() => updateStatus(a.id, 'cancelled')} disabled={updatingId === a.id}
                              className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5' }}>
                              ✕
                            </button>
                          )}
                        </>
                      )}
                      {/* Doctor/admin: case sheet + prescription */}
                      {(user.role === 'doctor' || user.role === 'admin') && (
                        <button onClick={() => setCurrentPage && setCurrentPage('medical-form')}
                          className="btn btn-primary" style={{ fontSize: 11, padding: '4px 8px' }}>
                          📋 Case Sheet
                        </button>
                      )}
                      {/* All clinical roles: view prescription */}
                      {(user.role === 'doctor' || user.role === 'admin' || user.role === 'receptionist') && (
                        <button onClick={() => setCurrentPage && setCurrentPage('prescriptions')}
                          className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                          💊 Prescription
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: 16, color: '#94a3b8', fontSize: 12 }}>
        Total: {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''}
        {hasFilters && ` (filtered from ${appointments.length})`}
      </p>

      {/* ── SLOT BOOKINGS SECTION ── */}
      <div style={{ marginTop: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h3 style={{ margin: 0 }}>📅 Scheduled Slot Bookings</h3>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>Slots booked through the service scheduling system.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Date:</label>
            <input type="date" value={slotDate} onChange={e => setSlotDate(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#1a1a1a', background: '#fff' }} />
            <button className="btn" style={{ fontSize: 13 }} onClick={fetchSlots}>🔄</button>
          </div>
        </div>

        {slotsLoading ? (
          <p style={{ color: '#64748b' }}>Loading slots…</p>
        ) : slots.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🗓️</div>
            <p style={{ fontSize: 14 }}>No booked slots for {new Date(slotDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f0fdf4' }}>
                  {['Time', 'Service', 'Patient Name', 'Phone', 'Notes', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', fontWeight: 600, textAlign: 'left', fontSize: 13, whiteSpace: 'nowrap', color: '#166534' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.map((slot, i) => (
                  <tr key={slot.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f9fffe' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: '#166534', whiteSpace: 'nowrap' }}>
                      {slot.time_start || '—'}
                    </td>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>
                      {slot.service_name || '—'}
                      {(slot.additional_service_names || []).length > 0 && (
                        <div style={{ fontSize: 11, color: '#6366f1', marginTop: 2 }}>
                          +{slot.additional_service_names.join(', ')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px' }}>{slot.patient_name || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {slot.patient_phone
                        ? <a href={`tel:${slot.patient_phone}`} style={{ color: '#0891b2', textDecoration: 'none' }}>{slot.patient_phone}</a>
                        : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slot.notes || '—'}</td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {/* Reschedule */}
                        {(user.role === 'doctor' || user.role === 'admin' || user.role === 'receptionist') && (
                          <button onClick={() => openReschedule(slot)}
                            className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#fef9c3', color: '#92400e', border: '1px solid #fcd34d' }}>
                            📅 Reschedule
                          </button>
                        )}
                        {/* Release / delete booking */}
                        {(user.role === 'admin' || user.role === 'receptionist') && (
                          <button onClick={() => handleReleaseSlot(slot)}
                            className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5' }}>
                            🗑 Release
                          </button>
                        )}
                        {(user.role === 'doctor' || user.role === 'admin') && (
                          <button onClick={() => setCurrentPage && setCurrentPage('medical-form')}
                            className="btn btn-primary" style={{ fontSize: 11, padding: '4px 8px' }}>
                            📋 Case Sheet
                          </button>
                        )}
                        {(user.role === 'doctor' || user.role === 'admin' || user.role === 'receptionist') && (
                          <button onClick={() => setCurrentPage && setCurrentPage('invoices')}
                            className="btn" style={{ fontSize: 11, padding: '4px 8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                            🧾 Invoice
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Reschedule modal (AppointmentsPage) ── */}
      {rescheduleTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setRescheduleTarget(null); }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 24px',
            maxWidth: 420, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            <h3 style={{ color: '#0f172a', fontSize: 18, marginBottom: 4 }}>📅 Reschedule Slot</h3>
            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
              Patient: <strong>{rescheduleTarget.patient_name}</strong> · Currently: <strong>{rescheduleTarget.date} at {rescheduleTarget.time_start}</strong>
            </p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 5 }}>New Date</label>
                <input type="date" value={rsDate} onChange={e => setRsDate(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 5 }}>New Time</label>
                <select value={rsTime} onChange={e => setRsTime(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff' }}>
                  {Array.from({ length: 24 }, (_, i) => {
                    const h = Math.floor(i / 2) + 8;
                    const m = i % 2 === 0 ? '00' : '30';
                    if (h >= 20) return null;
                    const t = `${String(h).padStart(2,'0')}:${m}`;
                    return <option key={t} value={t}>{t}</option>;
                  })}
                </select>
              </div>
            </div>

            {(rsDate !== rescheduleTarget.date || rsTime !== rescheduleTarget.time_start) && (
              <div style={{ background: '#fef9c3', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                ⚠️ Moving from <strong>{rescheduleTarget.date} {rescheduleTarget.time_start}</strong> → <strong>{rsDate} {rsTime}</strong>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleReschedule} disabled={rsLoading}
                style={{ padding: '10px 22px', background: rsLoading ? '#94a3b8' : '#2C5F2D',
                  color: '#fff', border: 'none', borderRadius: 6, cursor: rsLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14 }}>
                {rsLoading ? 'Saving…' : '✅ Confirm Reschedule'}
              </button>
              <button onClick={() => setRescheduleTarget(null)}
                style={{ padding: '10px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== REPORTS PAGE COMPONENT ====================
export function ReportsPage() {
  const [loading, setLoading]   = useState(false);
  const [message, setMessage]   = useState({ text: '', type: '' });

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleGenerateReport = async (type) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/reports/export?type=${type}`, { headers: getAuthHeader() });
      if (response.ok) {
        const blob = await response.blob();
        const url  = window.URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `${type}_report_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showMsg(`${type} report downloaded successfully.`);
      } else {
        const data = await response.json().catch(() => ({}));
        showMsg(data.message || `Report generation failed (HTTP ${response.status}).`, 'error');
      }
    } catch (error) {
      showMsg('Network error — could not generate report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const REPORTS = [
    { type: 'patients',        icon: '📋', label: 'Patient Report' },
    { type: 'medical_records', icon: '🏥', label: 'Medical Records' },
    { type: 'invoices',        icon: '💰', label: 'Financial Report' },
    { type: 'prescriptions',   icon: '💊', label: 'Prescriptions Report' },
  ];

  return (
    <div className="page-container" style={{ maxWidth: 800 }}>
      <h2>📊 Generate Reports</h2>
      <p style={{ color: '#64748b', marginTop: 4 }}>Download data exports as Excel files.</p>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ margin: '16px 0' }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginTop: 24 }}>
        {REPORTS.map(r => (
          <button key={r.type} onClick={() => handleGenerateReport(r.type)} disabled={loading}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              padding: '24px 16px', border: '1px solid #e2e8f0', borderRadius: 10,
              background: loading ? '#f8fafc' : '#fff', cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 500, color: '#1e293b' }}
            onMouseOver={e => { if (!loading) e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; }}
            onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; }}>
            <span style={{ fontSize: 32 }}>{r.icon}</span>
            {r.label}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: '#64748b', marginTop: 20, textAlign: 'center' }}>⏳ Generating report…</p>}
    </div>
  );
}


// ==================== PATIENT HISTORY COMPONENT ====================

export function PatientHistory({ user }) {
  const [patients, setPatients]             = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [selectedDbId, setSelectedDbId]     = useState('');
  const [selectedInfo, setSelectedInfo]     = useState(null);
  const [records, setRecords]               = useState([]);
  const [prescriptions, setPrescriptions]   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [expandedRec, setExpandedRec]       = useState(null);

  useEffect(() => { fetchPatients(); }, []);

  const fetchPatients = async () => {
    setPatientsLoading(true);
    try {
      const res = await fetch(`${API_URL}/patients`, { headers: getAuthHeader() });
      const d = await res.json();
      setPatients(d.patients || []);
    } catch (e) { console.error(e); }
    finally { setPatientsLoading(false); }
  };

  const fetchHistory = async (dbId) => {
    setLoading(true);
    setRecords([]);
    setPrescriptions([]);
    setExpandedRec(null);
    try {
      const [mrRes, rxRes] = await Promise.all([
        fetch(`${API_URL}/medical-records/patient/${dbId}`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/prescriptions?patient_id=${dbId}`, { headers: getAuthHeader() }),
      ]);
      const mrData = await mrRes.json();
      const rxData = await rxRes.json();
      setRecords(mrData.records || []);
      setPrescriptions(rxData.prescriptions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const getRxForRecord = (recId) => prescriptions.find(rx => rx.medical_record_id === recId);

  return (
    <div className="page-container" style={{ maxWidth: 1000 }}>
      <h2 style={{ marginBottom: 6 }}>📜 Patient History</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>
        Search a patient to view all their past case sheets and prescriptions.
      </p>

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
        {patientsLoading ? (
          <p style={{ color: '#64748b', fontSize: 13 }}>Loading patient list…</p>
        ) : (
          <PatientSelect
            patients={patients}
            value={selectedDbId}
            onChange={(dbId, pat) => {
              setSelectedDbId(dbId);
              setSelectedInfo(pat);
              if (dbId) fetchHistory(dbId);
              else { setRecords([]); setPrescriptions([]); }
            }}
            label="Search Patient"
          />
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>⏳ Loading history…</div>
      )}

      {!loading && selectedDbId && records.length === 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 20, textAlign: 'center', color: '#92400e' }}>
          No case sheets found for this patient yet.
        </div>
      )}

      {!loading && records.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ color: '#1e293b', fontSize: 15 }}>
              {records.length} Visit{records.length !== 1 ? 's' : ''} on record for <strong>{selectedInfo?.name}</strong>
            </h3>
          </div>

          {records.slice().reverse().map((rec) => {
            const rx = getRxForRecord(rec.id);
            const isOpen = expandedRec === rec.id;
            return (
              <div key={rec.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
                {/* Header row */}
                <div
                  onClick={() => setExpandedRec(isOpen ? null : rec.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 18px', cursor: 'pointer', background: isOpen ? '#f0fdf4' : '#f8fafc',
                    borderBottom: isOpen ? '1px solid #d1fae5' : 'none' }}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>📅 {fmtDate(rec.visit_date)}</span>
                    {rec.is_follow_up && <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Follow-up</span>}
                    {rx && <span style={{ background: '#f0fdf4', color: '#15803d', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>💊 Prescription: {rx.prescription_id}</span>}
                  </div>
                  <span style={{ color: '#64748b', fontSize: 13 }}>{isOpen ? '▲ Collapse' : '▼ Expand'}</span>
                </div>

                {/* Detail body */}
                {isOpen && (
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                      {[
                        ['Chief Complaint', rec.chief_complaint],
                        ['Diagnosis', rec.diagnosis],
                        ['Previous History', rec.previous_medical_history],
                        ['Family History', rec.family_history],
                        ['Current Medications', rec.current_medications],
                        ['Examination Findings', rec.examination_findings],
                        ['Treatment Notes', rec.treatment_notes],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label} style={{ background: '#f8fafc', borderRadius: 6, padding: '10px 14px' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
                          <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{value}</div>
                        </div>
                      ))}
                    </div>

                    {rx && (
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '14px 18px', marginTop: 8 }}>
                        <div style={{ fontWeight: 700, color: '#15803d', fontSize: 13, marginBottom: 10 }}>💊 Prescription — {rx.prescription_id}</div>
                        {(rx.oral_medications || []).length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Oral Medications</div>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {(rx.oral_medications || []).map((m, i) => <li key={i} style={{ fontSize: 13, color: '#1e293b', marginBottom: 3 }}>{m}</li>)}
                            </ul>
                          </div>
                        )}
                        {(rx.panchakarma_therapies || []).length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Therapies</div>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {(rx.panchakarma_therapies || []).map((t, i) => <li key={i} style={{ fontSize: 13, color: '#1e293b', marginBottom: 3 }}>{t}</li>)}
                            </ul>
                          </div>
                        )}
                        {rx.dietary_advice && (
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Dietary Advice: </span>
                            <span style={{ fontSize: 13, color: '#1e293b' }}>{rx.dietary_advice}</span>
                          </div>
                        )}
                        {rx.lifestyle_advice && (
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Lifestyle Advice: </span>
                            <span style={{ fontSize: 13, color: '#1e293b' }}>{rx.lifestyle_advice}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                          <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px' }}
                            onClick={() => printPrescription(rx, selectedInfo)}>
                            🖨 Print Prescription
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ==================== SLOT SCHEDULER COMPONENT ====================
// Full day grid: all half-hour slots 8am–8pm shown as placeholders.
// Click any slot to book it for a patient (service + optional notes).
// Click a booked slot to view details or release it.

// Generate 08:00–19:30 in 30-min steps
const DAY_GRID = (() => {
  const times = [];
  for (let h = 8; h < 20; h++) {
    times.push(`${String(h).padStart(2, '0')}:00`);
    times.push(`${String(h).padStart(2, '0')}:30`);
  }
  return times;
})();

export function SlotScheduler({ user }) {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [bookings, setBookings]         = useState([]);
  const [services, setServices]         = useState([]);
  const [patients, setPatients]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [message, setMessage]           = useState({ text: '', type: '' });

  // Modal state
  const [activeTime, setActiveTime]     = useState(null);
  const [activeSlot, setActiveSlot]     = useState(null);
  const [editMode, setEditMode]         = useState(false);  // false = view, true = editing existing slot
  const [bookForm, setBookForm]         = useState({ patient_id: '', service_id: '', notes: '' });
  const [rescheduleDate, setRescheduleDate] = useState('');  // for rescheduling in edit mode
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [extraServices, setExtraServices] = useState([]);  // additional service chips
  const [extraPickValue, setExtraPickValue] = useState('');
  const [bookLoading, setBookLoading]   = useState(false);

  useEffect(() => { fetchAll(); }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [slotRes, svcRes, patRes] = await Promise.all([
        fetch(`${API_URL}/slots?date=${selectedDate}`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/services`,                   { headers: getAuthHeader() }),
        fetch(`${API_URL}/patients`,                   { headers: getAuthHeader() }),
      ]);
      setBookings((await slotRes.json()).slots || []);
      setServices((await svcRes.json()).services || []);
      setPatients((await patRes.json()).patients || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const addExtraService = (svcId) => {
    if (!svcId) return;
    const svc = services.find(s => String(s.id) === String(svcId));
    if (!svc) return;
    if (extraServices.find(e => e.id === svc.id)) return;
    setExtraServices(prev => [...prev, { id: svc.id, service_name: svc.service_name }]);
    setExtraPickValue('');
  };

  const removeExtraService = (id) => setExtraServices(prev => prev.filter(e => e.id !== id));

  const openTime = (time) => {
    const existing = bookings.find(b => b.time_start === time);
    setActiveTime(time);
    setActiveSlot(existing || null);
    setEditMode(false);
    setBookForm({ patient_id: '', service_id: '', notes: '' });
    setExtraServices([]);
    setExtraPickValue('');
  };

  const enterEditMode = () => {
    if (!activeSlot) return;
    setBookForm({
      patient_id: String(activeSlot.patient_id || ''),
      service_id: String(activeSlot.service_id || ''),
      notes:      activeSlot.notes || '',
    });
    setRescheduleDate(activeSlot.date || selectedDate);
    setRescheduleTime(activeSlot.time_start || activeTime);
    // Populate additional services chips from existing slot
    const extras = (activeSlot.additional_service_ids || []).map(id => {
      const svc = services.find(s => s.id === id);
      return svc ? { id: svc.id, service_name: svc.service_name } : null;
    }).filter(Boolean);
    setExtraServices(extras);
    setExtraPickValue('');
    setEditMode(true);
  };

  const handleBook = async () => {
    if (!bookForm.patient_id) { showMsg('Please select a patient.', 'error'); return; }
    setBookLoading(true);
    try {
      const res = await fetch(`${API_URL}/slots`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({
          date:                selectedDate,
          time_start:          activeTime,
          service_id:          bookForm.service_id ? parseInt(bookForm.service_id) : null,
          additional_services: extraServices.map(e => e.id),
          patient_id:          parseInt(bookForm.patient_id),
          notes:               bookForm.notes || '',
        }),
      });
      const d = await res.json();
      if (res.ok) { showMsg('Slot booked successfully!'); closeModal(); fetchAll(); }
      else { showMsg(d.message || 'Failed to book slot.', 'error'); }
    } catch { showMsg('Network error.', 'error'); }
    finally { setBookLoading(false); }
  };

  const handleSaveEdit = async () => {
    if (!activeSlot) return;
    setBookLoading(true);
    try {
      const body = {
        edit:                true,
        date:                rescheduleDate || activeSlot.date,
        time_start:          rescheduleTime || activeSlot.time_start,
        service_id:          bookForm.service_id ? parseInt(bookForm.service_id) : null,
        additional_services: extraServices.map(e => e.id),
        patient_id:          bookForm.patient_id ? parseInt(bookForm.patient_id) : activeSlot.patient_id,
        notes:               bookForm.notes || '',
      };
      const res = await fetch(`${API_URL}/slots/${activeSlot.id}`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (res.ok) { showMsg('Slot updated successfully!'); closeModal(); fetchAll(); }
      else { showMsg(d.message || 'Failed to update slot.', 'error'); }
    } catch { showMsg('Network error.', 'error'); }
    finally { setBookLoading(false); }
  };

  const handleRelease = async (slotId) => {
    if (!window.confirm('Release this slot? The patient booking will be removed.')) return;
    try {
      const res = await fetch(`${API_URL}/slots/${slotId}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      if (res.ok) { showMsg('Slot released — now available again.'); closeModal(); fetchAll(); }
      else { const d = await res.json(); showMsg(d.message || 'Failed.', 'error'); }
    } catch { showMsg('Network error.', 'error'); }
  };

  const closeModal = () => {
    setActiveTime(null);
    setActiveSlot(null);
    setEditMode(false);
    setExtraServices([]);
    setExtraPickValue('');
    setRescheduleDate('');
    setRescheduleTime('');
  };

  const bookedCount    = bookings.filter(b => b.status === 'booked').length;
  const availableCount = DAY_GRID.length - bookedCount;

  // Reusable additional services picker UI
  const AdditionalServicesPicker = () => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 5, color: '#334155' }}>
        Additional Services <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — add more)</span>
      </label>
      <select value={extraPickValue}
        onChange={e => addExtraService(e.target.value)}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff' }}>
        <option value="">+ Add another service…</option>
        {services.filter(s => s.is_active).map(s => (
          <option key={s.id} value={s.id}>{s.service_name}</option>
        ))}
      </select>
      {extraServices.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {extraServices.map(e => (
            <span key={e.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
              background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
              borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>
              {e.service_name}
              <button type="button" onClick={() => removeExtraService(e.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="page-container" style={{ maxWidth: 1100 }}>
      <h2 style={{ marginBottom: 6 }}>🗓 Appointment Slots</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        All time slots for the selected date are shown below. Click any <strong style={{ color: '#15803d' }}>Available</strong> slot
        to book it for a patient. Click a <strong style={{ color: '#1d4ed8' }}>Booked</strong> slot to view, edit or release it.
      </p>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {/* Controls row */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4, color: '#1e293b' }}>Date</label>
          <input type="date" value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#1e293b', background: '#fff' }} />
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ background: '#f0fdf4', color: '#15803d', padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1px solid #86efac' }}>
            ✅ {availableCount} Available
          </span>
          <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1px solid #bfdbfe' }}>
            📌 {bookedCount} Booked
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 16, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
        <span>🟢 Click green slot → book for patient</span>
        <span>🔵 Click blue slot → view / edit / release</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>⏳ Loading slots…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
          {DAY_GRID.map(time => {
            const slot   = bookings.find(b => b.time_start === time);
            const booked = slot && slot.status === 'booked';
            const hasExtra = booked && slot.additional_service_names && slot.additional_service_names.length > 0;
            return (
              <div
                key={time}
                onClick={() => openTime(time)}
                style={{
                  background:   booked ? '#eff6ff' : '#f0fdf4',
                  border:       `2px solid ${booked ? '#93c5fd' : '#6ee7b7'}`,
                  borderRadius: 10,
                  padding:      '12px 12px 10px',
                  cursor:       'pointer',
                  transition:   'transform 0.1s, box-shadow 0.1s',
                  userSelect:   'none',
                }}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.10)'; }}
                onMouseOut={e =>  { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>{time}</div>
                {booked ? (
                  <>
                    <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600, marginTop: 4 }}>📌 Booked</div>
                    <div style={{ fontSize: 11, color: '#334155', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      👤 {slot.patient_name}
                    </div>
                    {slot.service_name && (
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        🏥 {slot.service_name}
                      </div>
                    )}
                    {hasExtra && (
                      <div style={{ fontSize: 10, color: '#6366f1', marginTop: 2 }}>
                        +{slot.additional_service_names.length} more
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: '#15803d', fontWeight: 600, marginTop: 4 }}>✅ Available</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal ── */}
      {activeTime && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 28px 24px',
            maxWidth: 520, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>

            {activeSlot && activeSlot.status === 'booked' && !editMode ? (
              /* ── View booked slot ── */
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ color: '#0f172a', fontSize: 18, marginBottom: 2 }}>📌 {activeTime}</h3>
                    <p style={{ color: '#64748b', fontSize: 13 }}>{selectedDate}</p>
                  </div>
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Booked</span>
                </div>

                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px', marginBottom: 22 }}>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ fontSize: 14 }}>
                      <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Patient</span><br />
                      <strong style={{ color: '#0f172a' }}>{activeSlot.patient_name}</strong>
                      {activeSlot.patient_pid && <span style={{ color: '#64748b', fontSize: 12 }}> · {activeSlot.patient_pid}</span>}
                    </div>

                    {(activeSlot.service_name || (activeSlot.additional_service_names || []).length > 0) && (
                      <div style={{ fontSize: 14 }}>
                        <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Services</span><br />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                          {activeSlot.service_name && (
                            <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                              {activeSlot.service_name}
                            </span>
                          )}
                          {(activeSlot.additional_service_names || []).map((name, i) => (
                            <span key={i} style={{ background: '#f0fdf4', color: '#15803d', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {activeSlot.notes && (
                      <div style={{ fontSize: 13, color: '#475569' }}>
                        <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Notes</span><br />
                        <em>{activeSlot.notes}</em>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(user.role === 'admin' || user.role === 'receptionist' || user.role === 'doctor') && (
                    <button onClick={enterEditMode}
                      style={{ padding: '9px 18px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      ✏️ Edit Slot
                    </button>
                  )}
                  {(user.role === 'admin' || user.role === 'receptionist') && (
                    <button onClick={() => handleRelease(activeSlot.id)}
                      style={{ padding: '9px 18px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      ↩ Release Slot
                    </button>
                  )}
                  <button onClick={closeModal}
                    style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    Close
                  </button>
                </div>
              </>

            ) : (activeSlot && editMode) ? (
              /* ── Edit booked slot ── */
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ color: '#0f172a', fontSize: 18, marginBottom: 2 }}>✏️ Edit Slot — {activeTime}</h3>
                    <p style={{ color: '#64748b', fontSize: 13 }}>{selectedDate}</p>
                  </div>
                  <span style={{ background: '#fef9c3', color: '#92400e', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Editing</span>
                </div>

                {/* ── Reschedule section ── */}
                <div style={{ background: '#fef9c3', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                    📅 Reschedule To
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>Date</label>
                      <input type="date" value={rescheduleDate}
                        onChange={e => setRescheduleDate(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>Time</label>
                      <select value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff' }}>
                        {DAY_GRID.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  {(rescheduleDate !== activeSlot.date || rescheduleTime !== activeSlot.time_start) && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
                      ⚠️ Will move from <strong>{activeSlot.date} {activeSlot.time_start}</strong> → <strong>{rescheduleDate} {rescheduleTime}</strong>
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 5, color: '#334155' }}>
                    Primary Service <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                  </label>
                  <select value={bookForm.service_id}
                    onChange={e => setBookForm(p => ({ ...p, service_id: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff' }}>
                    <option value="">— None —</option>
                    {services.filter(s => s.is_active).map(s => (
                      <option key={s.id} value={s.id}>{s.service_name}</option>
                    ))}
                  </select>
                </div>

                <AdditionalServicesPicker />

                <div style={{ marginBottom: 14 }}>
                  <PatientSelect
                    patients={patients}
                    value={bookForm.patient_id}
                    onChange={(dbId) => setBookForm(p => ({ ...p, patient_id: dbId }))}
                    required
                    label="Patient *"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 22 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 5, color: '#334155' }}>
                    Notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                  </label>
                  <input type="text" value={bookForm.notes}
                    onChange={e => setBookForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="e.g. first visit, bring reports…"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleSaveEdit} disabled={bookLoading}
                    style={{ padding: '10px 22px', background: bookLoading ? '#94a3b8' : 'var(--primary-color, #2C5F2D)',
                      color: '#fff', border: 'none', borderRadius: 6, cursor: bookLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 600, fontSize: 14 }}>
                    {bookLoading ? 'Saving…' : '💾 Save Changes'}
                  </button>
                  <button onClick={() => setEditMode(false)}
                    style={{ padding: '10px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                    ← Back
                  </button>
                </div>
              </>

            ) : (
              /* ── Book available slot ── */
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ color: '#0f172a', fontSize: 18, marginBottom: 2 }}>Book Slot — {activeTime}</h3>
                    <p style={{ color: '#64748b', fontSize: 13 }}>{selectedDate}</p>
                  </div>
                  <span style={{ background: '#f0fdf4', color: '#15803d', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Available</span>
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 5, color: '#334155' }}>
                    Primary Service <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                  </label>
                  <select value={bookForm.service_id}
                    onChange={e => setBookForm(p => ({ ...p, service_id: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff' }}>
                    <option value="">— Select a service —</option>
                    {services.filter(s => s.is_active).map(s => (
                      <option key={s.id} value={s.id}>{s.service_name}</option>
                    ))}
                  </select>
                </div>

                <AdditionalServicesPicker />

                <div style={{ marginBottom: 14 }}>
                  <PatientSelect
                    patients={patients}
                    value={bookForm.patient_id}
                    onChange={(dbId) => setBookForm(p => ({ ...p, patient_id: dbId }))}
                    required
                    label="Patient *"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 22 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 5, color: '#334155' }}>
                    Notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                  </label>
                  <input type="text" value={bookForm.notes}
                    onChange={e => setBookForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="e.g. first visit, bring reports…"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleBook} disabled={bookLoading || !bookForm.patient_id}
                    style={{ padding: '10px 22px', background: bookLoading || !bookForm.patient_id ? '#94a3b8' : 'var(--primary-color, #2C5F2D)',
                      color: '#fff', border: 'none', borderRadius: 6, cursor: bookLoading || !bookForm.patient_id ? 'not-allowed' : 'pointer',
                      fontWeight: 600, fontSize: 14 }}>
                    {bookLoading ? 'Booking…' : '✅ Confirm Booking'}
                  </button>
                  <button onClick={closeModal}
                    style={{ padding: '10px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
